import React, { useState, useRef, useEffect } from 'react';
import { Plus, Trash2, Edit2, Save, Bot, Clock, ToggleLeft, ToggleRight, X, Download, Upload, Image, Video, FileText, Paperclip, Menu as MenuIcon, Globe, ChevronDown, ChevronUp, Copy, CheckSquare, Square, Zap, Loader, Sparkles } from 'lucide-react';
import { AutoReplyRule, InteractiveMenu } from '../types';
import { countries as countryList } from '../utils/countries';
import { createAutoReplyRule, updateAutoReplyRule, deleteAutoReplyRule, importAutoReplyRules, getAutoReplyRules, getInteractiveMenus, getApiUrl, uploadOptionMedia, getAppConnections } from '../services/api';
import { MediaUpload } from './MediaUpload';
import { MessageEditorToolbar } from './MessageEditorToolbar';
import { MessagePreview } from './MessagePreview';
import { BulkProgressBar } from './BulkProgressBar';
import { ConfirmModal } from './ConfirmModal';
import { useMedia, MediaItem as UseMediaItem } from '../hooks/useMedia';
import { MediaThumbnail } from './MediaThumbnail';
import { GlobalSessionIndicator } from './GlobalSessionToggle';
import { useGlobalSessions } from '../hooks/useGlobalSessions';
import { ImportModal } from './ImportModal';
import { BulkRuleCreator } from './BulkRuleCreator';
import { useSession } from '../context/SessionContext';

interface AutoReplyManagerProps {
    rules: AutoReplyRule[];
    setRules: (rules: AutoReplyRule[]) => void;
    toast?: {
        success: (message: string) => void;
        error: (message: string) => void;
        warning: (message: string) => void;
        info: (message: string) => void;
    };
}

export const AutoReplyManager: React.FC<AutoReplyManagerProps> = ({ rules, setRules, toast }) => {
    const { selectedSession } = useSession();
    const [editingId, setEditingId] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [formErrors, setFormErrors] = useState<{ [key: string]: string }>({});
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [ruleToDelete, setRuleToDelete] = useState<string | null>(null);
    const [isExporting, setIsExporting] = useState(false);
    const [menus, setMenus] = useState<InteractiveMenu[]>([]);
    const [modelsList, setModelsList] = useState<string[]>([]);
    const [isLoadingModels, setIsLoadingModels] = useState(false);

    const fetchModels = async (provider: string) => {
        if (!provider || provider === 'auto' || provider === 'custom' || provider.startsWith('http')) {
            setModelsList([]);
            return;
        }
        try {
            setIsLoadingModels(true);
            const apiUrl = getApiUrl();
            const response = await fetch(`${apiUrl}/api/ai/models?provider=${provider}`, {
                headers: {
                    'x-session-id': localStorage.getItem('x-session-id') || '',
                    'x-user-id': localStorage.getItem('x-user-id') || ''
                }
            });
            if (response.ok) {
                const data = await response.json();
                if (data.success && Array.isArray(data.models)) {
                    setModelsList(data.models);
                }
            }
        } catch (error) {
            console.error('Error fetching models:', error);
        } finally {
            setIsLoadingModels(false);
        }
    };

    const [showImportModal, setShowImportModal] = useState(false);
    const [showBulkCreator, setShowBulkCreator] = useState(false);
    const [showCountrySelector, setShowCountrySelector] = useState(false);
    const [countrySearch, setCountrySearch] = useState('');
    const [countryTab, setCountryTab] = useState<'include' | 'exclude'>('include');
    const [showMobileEditor, setShowMobileEditor] = useState(false);
    const { globalSessionsEnabled } = useGlobalSessions();
    const [isLoadingCountryMedia, setIsLoadingCountryMedia] = useState<Record<string, boolean>>({});
    const [isCountryResponsesExpanded, setIsCountryResponsesExpanded] = useState(true);
    const [countryResponsesLayout, setCountryResponsesLayout] = useState<'1col' | '2col'>('1col');
    const [isOptimizingKB, setIsOptimizingKB] = useState(false);
    const [showBrowserAiModal, setShowBrowserAiModal] = useState(false);
    const [downloadProgress, setDownloadProgress] = useState<{ loaded: number, total: number } | null>(null);
    const [isDownloadingModel, setIsDownloadingModel] = useState(false);
    const [showOptimizeKbModal, setShowOptimizeKbModal] = useState(false);
    const [optMethod, setOptMethod] = useState<'local' | 'server'>('local');
    const [optLanguage, setOptLanguage] = useState<string>('Español');

    const [connections, setConnections] = useState<any[]>([]);

    useEffect(() => {
        loadConnections();
    }, []);

    const loadConnections = async () => {
        try {
            const res = await getAppConnections();
            if (res.success) {
                setConnections(res.connections || []);
            }
        } catch (err) {
            console.error('Error loading connections in AutoReplyManager:', err);
        }
    };

    // Load interactive menus
    useEffect(() => {
        loadMenus();
    }, [selectedSession?.sessionId]);



    const loadMenus = async () => {
        try {
            const response = await getInteractiveMenus();
            if (response.success) {
                setMenus(response.menus.filter((m: InteractiveMenu) => m.isActive));
            }
        } catch (error) {
            console.error('Error loading menus:', error);
        }
    };

    // Helper function to get media preview URL
    const getMediaPreviewUrl = (mediaPath: string): string => {
        if (mediaPath.startsWith('http')) {
            return mediaPath;
        }
        const fileName = mediaPath.split(/[/\\]/).pop() || '';
        return `${getApiUrl()}/uploads/${fileName}`;
    };

    // Helper function to get media type from path
    const getMediaTypeFromPath = (path: string): 'image' | 'video' | 'document' => {
        const ext = path.toLowerCase().split('.').pop() || '';
        if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg'].includes(ext)) return 'image';
        if (['mp4', 'avi', 'mov', 'webm', 'mkv', 'flv', 'wmv'].includes(ext)) return 'video';
        return 'document';
    };

    // Form State
    const [formData, setFormData] = useState<Partial<AutoReplyRule>>({
        name: '',
        keywords: [],
        response: '',
        matchType: 'contains',
        delay: 5,
        isActive: true,
        type: 'simple',
        countries: [],
        excludeCountries: [],
        allowUnknownCountries: false,
        isMessageCaption: false,
        countryResponses: {},
        aiProvider: 'auto',
        aiModel: 'auto',
        systemPrompt: '',
        aiApiKey: '',
        isCatchAll: false,
        isOrchestrator: false,
        orchestratorStrictMode: false,
    });

    useEffect(() => {
        if (formData.aiProvider) {
            fetchModels(formData.aiProvider);
        }
    }, [formData.aiProvider]);

    const [keywordInput, setKeywordInput] = useState('');
    const media = useMedia({ maxFiles: 50 });
    const kbMedia = useMedia({ maxFiles: 50 });
    const flyerMedia = useMedia({ maxFiles: 50 });
    const importInputRef = useRef<HTMLInputElement>(null);
    const messageTextareaRef = useRef<HTMLTextAreaElement>(null);
    const countryResponseRefs = useRef<{ [key: string]: HTMLTextAreaElement | null }>({});
    const countryCaptionRefs = useRef<{ [key: string]: HTMLTextAreaElement | null }>({});

    // Auto-resize response message textarea
    React.useEffect(() => {
        const textarea = messageTextareaRef.current;
        if (textarea) {
            textarea.style.height = 'auto';
            textarea.style.height = `${textarea.scrollHeight}px`;
        }
    }, [formData.response]);

    // Auto-resize country response and caption textareas
    React.useEffect(() => {
        if (!formData.countryResponses) return;

        Object.keys(formData.countryResponses).forEach(code => {
            // Resize response textareas
            const el = countryResponseRefs.current[code];
            if (el) {
                el.style.height = 'auto';
                el.style.height = `${el.scrollHeight}px`;
            }

            // Resize caption textareas for each media path
            const override = formData.countryResponses?.[code];
            if (override && override.mediaPaths) {
                override.mediaPaths.forEach((_, idx) => {
                    const key = `${code}-${idx}`;
                    const elCap = countryCaptionRefs.current[key];
                    if (elCap) {
                        elCap.style.height = 'auto';
                        elCap.style.height = `${elCap.scrollHeight}px`;
                    }
                });
            }
        });
    }, [formData.countryResponses]);

    const executeServerOptimization = async (lang: string = optLanguage) => {
        setIsOptimizingKB(true);
        const originalText = formData.knowledgeBaseText;

        try {
            if (toast) toast.info(`Optimizando usando la IA del servidor (usando tu proveedor de API en idioma ${lang})...`);

            const apiUrl = getApiUrl();
            const response = await fetch(`${apiUrl}/api/auto-reply/rules/optimize-kb`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'x-session-id': localStorage.getItem('x-session-id') || '',
                    'x-user-id': localStorage.getItem('x-user-id') || ''
                },
                body: JSON.stringify({ text: originalText, language: lang })
            });

            if (!response.ok) {
                const errData = await response.json();
                throw new Error(errData.error || 'Error en el servidor al optimizar');
            }

            const data = await response.json();
            if (data.success && data.optimizedText) {
                setFormData(prev => ({ ...prev, knowledgeBaseText: data.optimizedText }));
                if (toast) toast.success('¡Base de conocimientos optimizada exitosamente usando la IA del servidor!');
            } else {
                throw new Error('No se pudo optimizar el texto');
            }
        } catch (err: any) {
            console.error('Error optimizing KB on server:', err);
            if (toast) toast.error('Error al optimizar en el servidor: ' + err.message);
        } finally {
            setIsOptimizingKB(false);
        }
    };

    const getBrowserAISession = async () => {
        console.log('[Dev-AI] 🔍 Iniciando comprobación profunda de built-in AI...');

        // Log all possible namespaces
        console.log('[Dev-AI] typeof window.ai:', typeof (window as any).ai);
        console.log('[Dev-AI] typeof ai:', typeof (window as any).ai);
        console.log('[Dev-AI] typeof LanguageModel:', typeof (window as any).LanguageModel);
        console.log('[Dev-AI] typeof window.LanguageModel:', typeof (window as any).LanguageModel);

        // @ts-ignore
        const aiObj = (typeof window !== 'undefined' && ((window as any).ai || (window as any).model));

        // Helper to build options with monitor
        const getOptions = (sysPrompt: string, onProgress?: (loaded: number, total: number) => void) => {
            const options: any = {};
            if (sysPrompt) {
                options.systemPrompt = sysPrompt;
            }
            if (onProgress) {
                options.monitor = (m: any) => {
                    m.addEventListener("downloadprogress", (e: any) => {
                        onProgress(e.loaded, e.total);
                    });
                };
            }
            return options;
        };

        // 1. COMPROBACIÓN MODERNÍSIMA: Clase global LanguageModel directamente
        if (typeof (window as any).LanguageModel !== 'undefined') {
            console.log('[Dev-AI] ➡️ Detectado objeto global directo: LanguageModel. Comprobando disponibilidad...');
            try {
                // @ts-ignore
                const availability = await (window as any).LanguageModel.availability();
                console.log('[Dev-AI] Result of LanguageModel.availability():', availability);

                if (availability === 'available' || availability === 'after-download' || availability === 'downloadable') {
                    console.log(`[Dev-AI] 🎉 LanguageModel global está listo o habilitado (Estado: ${availability}).`);
                    const needsDownload = availability === 'after-download' || availability === 'downloadable';
                    return {
                        // @ts-ignore
                        create: (sysPrompt: string, onProgress?: (loaded: number, total: number) => void) =>
                            // @ts-ignore
                            (window as any).LanguageModel.create(getOptions(sysPrompt, onProgress)),
                        type: 'LanguageModel',
                        needsDownload
                    };
                } else {
                    console.warn(`[Dev-AI] ❌ LanguageModel.availability() devolvió: "${availability}".`);
                }
            } catch (e: any) {
                console.error('[Dev-AI] ❌ Error checking capabilities of LanguageModel global:', e);
            }
        }

        if (!aiObj) {
            console.warn('[Dev-AI] ❌ El objeto window.ai, global ai, ni LanguageModel existen en este navegador.');
            return null;
        }

        console.log('[Dev-AI] ✅ Objeto window.ai encontrado:', aiObj);
        console.log('[Dev-AI] Propiedades disponibles en window.ai:', Object.keys(aiObj));

        // 2. COMPROBACIÓN: ai.languageModel (Chrome 148+ estándar)
        if (aiObj.languageModel) {
            console.log('[Dev-AI] ➡️ Detectado espacio de nombres moderno en aiObj: languageModel. Comprobando capabilities...');
            try {
                const capabilities = await aiObj.languageModel.capabilities();
                console.log('[Dev-AI] Result of languageModel.capabilities():', capabilities);

                if (capabilities && capabilities.available !== 'no') {
                    console.log(`[Dev-AI] 🎉 languageModel está disponible (Estado: ${capabilities.available}).`);
                    const needsDownload = capabilities.available === 'after-download' || capabilities.available === 'downloadable';

                    return {
                        create: (sysPrompt: string, onProgress?: (loaded: number, total: number) => void) =>
                            aiObj.languageModel.create(getOptions(sysPrompt, onProgress)),
                        type: 'languageModel',
                        needsDownload
                    };
                } else {
                    console.warn('[Dev-AI] ❌ languageModel.capabilities().available es "no" o indefinido.');
                }
            } catch (e: any) {
                console.error('[Dev-AI] ❌ Error checking capabilities of languageModel:', e);
            }
        }

        // 3. COMPROBACIÓN: ai.assistant (Chrome 127 - 147 experimental)
        if (aiObj.assistant) {
            console.log('[Dev-AI] ➡️ Detectado espacio de nombres experimental en aiObj: assistant. Comprobando capabilities...');
            try {
                const capabilities = await aiObj.assistant.capabilities();
                console.log('[Dev-AI] Result of assistant.capabilities():', capabilities);

                if (capabilities && capabilities.available !== 'no') {
                    console.log(`[Dev-AI] 🎉 assistant está disponible (Estado: ${capabilities.available}).`);
                    const needsDownload = capabilities.available === 'after-download' || capabilities.available === 'downloadable';
                    return {
                        create: (sysPrompt: string, onProgress?: (loaded: number, total: number) => void) =>
                            aiObj.assistant.create(getOptions(sysPrompt, onProgress)),
                        type: 'assistant',
                        needsDownload
                    };
                } else {
                    console.warn('[Dev-AI] ❌ assistant.capabilities().available es "no" o indefinido.');
                }
            } catch (e: any) {
                console.error('[Dev-AI] ❌ Error checking capabilities of assistant:', e);
            }
        }

        // 4. COMPROBACIÓN LEGACY: window.ai.createTextSession
        if (aiObj.createTextSession) {
            console.log('[Dev-AI] ➡️ Detectada función legacy: createTextSession.');
            return {
                create: (sysPrompt: string, onProgress?: (loaded: number, total: number) => void) => aiObj.createTextSession(),
                type: 'createTextSession',
                needsDownload: false
            };
        }

        console.warn('[Dev-AI] ❌ window.ai está presente pero no tiene ningún método compatible (languageModel, assistant, ni createTextSession).');
        return null;
    };

    const handleOptimizeKB = async (method: 'local' | 'server' = optMethod, lang: string = optLanguage) => {
        if (!formData.knowledgeBaseText || formData.knowledgeBaseText.trim() === '') {
            if (toast) toast.warning('Escribe o pega información en la base de conocimientos primero.');
            return;
        }

        if (method === 'server') {
            await executeServerOptimization(lang);
            return;
        }

        try {
            const localAIService = await getBrowserAISession();
            if (localAIService) {
                setIsOptimizingKB(true);

                if (localAIService.needsDownload) {
                    setIsDownloadingModel(true);
                    if (toast) toast.info('El modelo Gemini Nano no está descargado. Iniciando descarga automática (~1.5 GB)...');
                } else {
                    if (toast) toast.info(`Preparando optimización usando la IA integrada de tu navegador (Chrome ${localAIService.type}) en idioma ${lang}...`);
                }

                const sysPrompt = `Eres un experto en optimización de bases de conocimientos. Tu tarea es resumir el texto proporcionado extrayendo únicamente los detalles clave, hechos concretos, precios, horarios, especificaciones y datos esenciales. Elimina el relleno, la redundancia y la palabrería inútil. Devuelve la información optimizada de forma extremadamente concisa, limpia y en el idioma ${lang} para que sirva de contexto para un chatbot de IA. No agregues introducciones, notas ni saludos, entrega el texto útil directo. Es ABSOLUTAMENTE CRÍTICO y OBLIGATORIO que toda tu respuesta esté redactada en el idioma: ${lang}.`;

                // @ts-ignore
                const session = await localAIService.create(sysPrompt, (loaded: number, total: number) => {
                    setDownloadProgress({ loaded, total });
                    console.log(`[Dev-AI] Descargando modelo: ${loaded}/${total} bytes (${Math.round((loaded / total) * 100)}%)`);
                });

                setIsDownloadingModel(false);
                setDownloadProgress(null);

                // Función de segmentación de texto para no exceder los límites de tokens de la IA local
                const text = formData.knowledgeBaseText;
                const maxChunkSize = 4000;
                const chunks: string[] = [];
                let currentChunk = '';

                // Dividir por líneas para no cortar oraciones ni palabras
                const paragraphs = text.split('\n');
                for (const paragraph of paragraphs) {
                    if ((currentChunk + '\n' + paragraph).length > maxChunkSize) {
                        if (currentChunk.trim() !== '') {
                            chunks.push(currentChunk.trim());
                            currentChunk = paragraph;
                        } else {
                            // Si un solo párrafo es ridículamente grande, lo forzamos a cortar
                            let remaining = paragraph;
                            while (remaining.length > maxChunkSize) {
                                chunks.push(remaining.substring(0, maxChunkSize));
                                remaining = remaining.substring(maxChunkSize);
                            }
                            currentChunk = remaining;
                        }
                    } else {
                        currentChunk = currentChunk ? currentChunk + '\n' + paragraph : paragraph;
                    }
                }
                if (currentChunk.trim() !== '') {
                    chunks.push(currentChunk.trim());
                }

                if (toast && chunks.length > 1) {
                    toast.info(`Optimizando base de conocimientos en ${chunks.length} partes por su longitud...`);
                }

                const optimizedResults: string[] = [];
                for (let i = 0; i < chunks.length; i++) {
                    if (toast && chunks.length > 1) {
                        toast.info(`Procesando parte ${i + 1} de ${chunks.length}...`);
                    }
                    // @ts-ignore
                    const chunkResult = await session.prompt(chunks[i]);
                    if (chunkResult && chunkResult.trim() !== '') {
                        optimizedResults.push(chunkResult.trim());
                    }
                }

                const finalResult = optimizedResults.join('\n\n');
                if (finalResult && finalResult.trim() !== '') {
                    setFormData(prev => ({ ...prev, knowledgeBaseText: finalResult.trim() }));
                    if (toast) toast.success('¡Base de conocimientos optimizada exitosamente usando la IA local de tu navegador!');
                    setIsOptimizingKB(false);
                    return;
                }
            }
        } catch (browserAiErr: any) {
            console.error('La IA local del navegador falló o la descarga se detuvo:', browserAiErr);
            setIsDownloadingModel(false);
            setDownloadProgress(null);
            if (toast) toast.warning('La descarga local falló o requiere más tiempo. Abriendo opciones del servidor...');
        } finally {
            setIsOptimizingKB(false);
        }

        // Si no está disponible localmente o falló, abrir el modal informativo y pedir autorización
        setShowBrowserAiModal(true);
    };

    const resetForm = () => {
        setFormData({
            name: '',
            keywords: [],
            response: '',
            matchType: 'contains',
            delay: 5,
            isActive: true,
            type: 'simple',
            countries: [],
            excludeCountries: [],
            allowUnknownCountries: false,
            isMessageCaption: false,
            countryResponses: {},
            aiProvider: 'auto',
            aiModel: 'auto',
            systemPrompt: '',
            aiApiKey: '',
            isCatchAll: false,
            isOrchestrator: false,
            orchestratorStrictMode: false,
            knowledgeBaseText: ''
        });
        setKeywordInput('');
        media.setMediaItems([]);
        kbMedia.setMediaItems([]);
        flyerMedia.setMediaItems([]);
        setEditingId(null);
        setShowMobileEditor(false);
    };

    const handleCountryMediaSelect = async (e: React.ChangeEvent<HTMLInputElement>, code: string) => {
        const fileList = e.target.files;
        if (!fileList || fileList.length === 0) return;
        const filesArray = Array.from(fileList) as File[];

        try {
            setIsLoadingCountryMedia(prev => ({ ...prev, [code]: true }));
            const res = await uploadOptionMedia(filesArray);
            if (res.success && res.files) {
                const newPaths = res.files.map((f: any) => `uploads/${f.filename}`);

                const prevOverride = formData.countryResponses?.[code] || { response: '', mediaPaths: [], captions: [] };
                const currentPaths = prevOverride.mediaPaths || [];
                const currentCaptions = prevOverride.captions || [];

                const updatedPaths = [...currentPaths, ...newPaths];
                const updatedCaptions = [...currentCaptions, ...newPaths.map(() => '')];

                setFormData(prev => ({
                    ...prev,
                    countryResponses: {
                        ...(prev.countryResponses || {}),
                        [code]: {
                            ...prevOverride,
                            mediaPaths: updatedPaths,
                            captions: updatedCaptions
                        }
                    }
                }));

                if (toast) {
                    const message = filesArray.length > 1
                        ? `${filesArray.length} archivos subidos exitosamente`
                        : "Archivo subido exitosamente";
                    toast.success(message);
                }
            }
        } catch (err: any) {
            console.error("Error uploading country media:", err);
            if (toast) {
                toast.error("Error al subir archivos: " + err.message);
            }
        } finally {
            setIsLoadingCountryMedia(prev => ({ ...prev, [code]: false }));
            e.target.value = ''; // Reset input to allow selecting the same files again
        }
    };

    const handleEdit = (rule: AutoReplyRule) => {
        setEditingId(rule.id);
        setShowMobileEditor(true);
        setFormData({
            ...rule,
            countries: rule.countries || [],
            excludeCountries: rule.excludeCountries || [],
            countryResponses: rule.countryResponses || {},
            knowledgeBaseText: rule.knowledgeBaseText || ''
        });
        const hasCountries = (rule.countries && rule.countries.length > 0) || (rule.excludeCountries && rule.excludeCountries.length > 0);
        setShowCountrySelector(hasCountries);

        // Auto-switch to exclude tab if only excluded countries are present
        if (rule.excludeCountries && rule.excludeCountries.length > 0 && (!rule.countries || rule.countries.length === 0)) {
            setCountryTab('exclude');
        } else {
            setCountryTab('include');
        }

        // Normalize keywords to array before joining
        let keywordsArray: string[] = [];
        if (Array.isArray(rule.keywords)) {
            keywordsArray = rule.keywords;
        } else if (typeof rule.keywords === 'string') {
            try {
                keywordsArray = JSON.parse(rule.keywords as string);
            } catch {
                keywordsArray = (rule.keywords as string)
                    .split(',')
                    .map((k: string) => k.trim())
                    .filter((k: string) => k.length > 0);
            }
        }
        setKeywordInput(keywordsArray.join(', '));

        // Helper to infer media type from path
        const getMediaTypeFromPath = (path: string): 'image' | 'video' | 'document' => {
            const ext = path.toLowerCase().split('.').pop() || '';
            if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg'].includes(ext)) return 'image';
            if (['mp4', 'avi', 'mov', 'webm', 'mkv', 'flv', 'wmv'].includes(ext)) return 'video';
            return 'document';
        };

        // Cargar múltiples medias si existen en la regla (mediaPaths + captions)
        const kbItems: UseMediaItem[] = [];
        const flyerItems: UseMediaItem[] = [];
        const simpleItems: UseMediaItem[] = [];

        if (Array.isArray((rule as any).mediaPaths) && (rule as any).mediaPaths.length > 0) {
            const mediaPaths: string[] = (rule as any).mediaPaths.filter((p: any) => !!p);

            // Get captions array - each file should have its own caption or empty string
            const captionsArray: string[] = Array.isArray((rule as any).captions)
                ? (rule as any).captions
                : mediaPaths.map(() => ''); // Empty string for each file if no captions array

            mediaPaths.forEach((mp, index) => {
                const fileName = mp.split(/[/\\]/).pop() || 'archivo';
                const previewUrl = mp.startsWith('http')
                    ? mp
                    : `${getApiUrl()}/uploads/${mp.replace(/^.*[\\/]/, '')}`;

                const item: UseMediaItem = {
                    preview: previewUrl,
                    caption: captionsArray[index] || '', // Use specific caption or empty
                    type: getMediaTypeFromPath(mp),
                    mediaPath: mp,
                    fileName
                };

                const ext = mp.toLowerCase().split('.').pop() || '';
                if (['txt', 'md', 'csv'].includes(ext)) {
                    kbItems.push(item);
                } else {
                    flyerItems.push(item);
                }
                simpleItems.push(item);
            });
        } else if (rule.mediaPath) {
            // Compatibilidad: una sola mediaPath antigua
            const mp = rule.mediaPath;
            const fileName = mp.split(/[/\\]/).pop() || 'archivo';
            const previewUrl = mp.startsWith('http')
                ? mp
                : `${getApiUrl()}/uploads/${mp.replace(/^.*[\\/]/, '')}`;

            const mediaItem: UseMediaItem = {
                preview: previewUrl,
                caption: rule.caption || '',
                type: getMediaTypeFromPath(mp),
                mediaPath: mp,
                fileName
            };

            const ext = mp.toLowerCase().split('.').pop() || '';
            if (['txt', 'md', 'csv'].includes(ext)) {
                kbItems.push(mediaItem);
            } else {
                flyerItems.push(mediaItem);
            }
            simpleItems.push(mediaItem);
        }

        if (rule.type === 'ai') {
            kbMedia.setMediaItems(kbItems);
            flyerMedia.setMediaItems(flyerItems);
            media.setMediaItems([]);
        } else {
            media.setMediaItems(simpleItems);
            kbMedia.setMediaItems([]);
            flyerMedia.setMediaItems([]);
        }
    };

    const handleSave = async () => {
        const errors: { [key: string]: string } = {};

        if (!formData.name) {
            errors.name = 'Nombre es requerido';
        }

        // Validate based on type
        if (formData.type === 'menu') {
            // Menu type requires menuId
            if (!formData.menuId) {
                errors.menuId = 'Debes seleccionar un menú';
            }
        } else if (formData.type === 'ai') {
            // AI type requires systemPrompt
            if (!formData.systemPrompt) {
                errors.systemPrompt = 'El prompt del sistema es requerido';
            }
        } else {
            // Simple type requires response or media
            if (!formData.response && media.mediaItems.length === 0) {
                errors.response = 'Respuesta o archivo multimedia es requerido';
            }
        }

        const processedKeywords = keywordInput.split(',').map(k => k.trim()).filter(k => k.length > 0);
        if (processedKeywords.length === 0 && formData.type !== 'ai') {
            errors.keywords = 'Debes agregar al menos una palabra clave';
        }

        if (Object.keys(errors).length > 0) {
            setFormErrors(errors);
            return;
        }

        setFormErrors({});

        setIsLoading(true);
        try {
            // Construir arrays de archivos y captions
            let allMediaItems = [];
            if (formData.type === 'ai') {
                allMediaItems = [...kbMedia.mediaItems, ...flyerMedia.mediaItems];
            } else {
                allMediaItems = media.mediaItems;
            }

            const files = allMediaItems
                .map(item => item.file)
                .filter((f): f is File => !!f);
            const captions = allMediaItems.map(item => item.caption || '');

            // Collect existing media paths that are already on the server
            const existingMediaPaths = allMediaItems
                .map(item => item.mediaPath)
                .filter((p): p is string => !!p);

            console.log('[AutoReplyManager] handleSave - files:', files, 'captions:', captions, 'existingMediaPaths:', existingMediaPaths);

            const ruleData = {
                name: formData.name!,
                keywords: processedKeywords,
                response: formData.response || '',
                matchType: formData.matchType as 'exact' | 'contains',
                delay: formData.delay || 0,
                isActive: formData.isActive ?? true,
                caption: captions[0] || '',
                captions,
                type: formData.type || 'simple',
                menuId: formData.menuId,
                countries: formData.countries || [],
                excludeCountries: formData.excludeCountries || [],
                allowUnknownCountries: formData.allowUnknownCountries ?? false,
                isMessageCaption: formData.isMessageCaption ?? false,
                countryResponses: formData.countryResponses || {},
                aiProvider: ['auto', 'google', 'openai', 'anthropic', 'xai'].includes(formData.aiProvider || '') ? formData.aiProvider : 'auto',
                aiModel: formData.aiModel,
                systemPrompt: formData.systemPrompt,
                aiApiKey: formData.aiApiKey,
                isCatchAll: formData.isCatchAll,
                isOrchestrator: formData.isOrchestrator,
                orchestratorStrictMode: formData.isOrchestrator ? (formData.orchestratorStrictMode ?? false) : false,
                knowledgeBaseText: formData.knowledgeBaseText || ''
            };

            if (editingId) {
                // Si editamos y no hay nuevos archivos, pasamos existingMediaPaths para preservar los actuales
                console.log('[AutoReplyManager] updateAutoReplyRule payload:', { id: editingId, ruleData, files, existingMediaPaths });
                const response = await updateAutoReplyRule(editingId, ruleData, files, existingMediaPaths);
                if (response.success) {
                    let updatedRules = rules.map(r => r.id === editingId ? response.rule : r);
                    if (response.rule.isOrchestrator) {
                        updatedRules = updatedRules.map(r => r.id !== editingId ? { ...r, isOrchestrator: false, isCatchAll: false, orchestratorStrictMode: false } : r);
                    }
                    setRules(updatedRules);
                    resetForm();
                }
            } else {
                console.log('[AutoReplyManager] createAutoReplyRule payload:', { ruleData, files, captions });
                const response = await createAutoReplyRule(ruleData, files, captions);
                if (response.success) {
                    let updatedRules = [...rules, response.rule];
                    if (response.rule.isOrchestrator) {
                        updatedRules = updatedRules.map(r => r.id !== response.rule.id ? { ...r, isOrchestrator: false, isCatchAll: false, orchestratorStrictMode: false } : r);
                    }
                    setRules(updatedRules);
                    resetForm();
                    setTimeout(() => {
                        const element = document.getElementById(`rule-container-${response.rule.id}`);
                        if (element) {
                            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
                        }
                    }, 100);
                }
            }
        } catch (error: any) {
            console.error('Error saving rule:', error);
            if (toast) {
                toast.error('Error al guardar la regla: ' + error.message);
            } else {
                setFormErrors({ general: 'Error al guardar la regla: ' + error.message });
            }
        } finally {
            setIsLoading(false);
        }
    };

    const handleDelete = (id: string) => {
        setRuleToDelete(id);
        setShowDeleteModal(true);
    };

    const confirmDelete = async () => {
        if (!ruleToDelete) return;

        const rule = rules.find(r => r.id === ruleToDelete);
        const ruleName = rule?.name || 'esta regla';

        try {
            setIsLoading(true);
            await deleteAutoReplyRule(ruleToDelete);
            setRules(rules.filter(r => r.id !== ruleToDelete));
            if (toast) {
                toast.success(`Regla "${ruleName}" eliminada exitosamente`);
            }
        } catch (error: any) {
            console.error('Error deleting rule:', error);
            if (toast) {
                toast.error('Error al eliminar la regla: ' + error.message);
            }
        } finally {
            setIsLoading(false);
            setShowDeleteModal(false);
            setRuleToDelete(null);
        }
    };

    const handleDuplicate = async (rule: AutoReplyRule) => {
        try {
            setIsLoading(true);
            const { id, ...rest } = rule;
            const duplicateName = `${rule.name} (copia)`;

            // For simple rules with media, we might need the server to support existingMediaPaths in POST
            // Current createAutoReplyRule in api.ts doesn't send existingMediaPaths
            // But we can try to send it in the rule object if the server doesn't overwrite it
            // Or better, we modify api.ts and the server later.

            const ruleData = {
                ...rest,
                name: duplicateName,
                isActive: true
            };

            // We pass [] for files and try to preserve captions
            const response = await createAutoReplyRule(ruleData, [], rule.captions);
            if (response.success) {
                setRules([...rules, response.rule]);
                if (toast) {
                    toast.success(`Regla "${rule.name}" duplicada como "${duplicateName}"`);
                }
                setTimeout(() => {
                    const element = document.getElementById(`rule-container-${response.rule.id}`);
                    if (element) {
                        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    }
                }, 100);
            }
        } catch (error: any) {
            console.error('Error duplicating rule:', error);
            if (toast) {
                toast.error('Error al duplicar la regla: ' + error.message);
            }
        } finally {
            setIsLoading(false);
        }
    };

    const toggleStatus = async (id: string) => {
        const rule = rules.find(r => r.id === id);
        if (!rule) return;

        try {
            setIsLoading(true);
            const updatedRule = { ...rule, isActive: !rule.isActive };
            const existingMediaPaths = (rule as any).mediaPaths ? (rule as any).mediaPaths.filter((p: string) => !!p) : (rule.mediaPath ? [rule.mediaPath] : []);
            const response = await updateAutoReplyRule(id, updatedRule, [], existingMediaPaths);
            if (response.success) {
                setRules(rules.map(r => r.id === id ? response.rule : r));
                if (toast) {
                    const newStatus = response.rule.isActive;
                    toast.success(`Regla "${rule.name}" ${newStatus ? 'activada' : 'desactivada'} exitosamente`);
                }
            }
        } catch (error: any) {
            console.error('Error toggling status:', error);
            if (toast) {
                toast.error('Error al actualizar estado: ' + error.message);
            }
        } finally {
            setIsLoading(false);
        }
    };

    // Export rules to JSON or ZIP (with media files)
    const handleExportRules = async () => {
        setIsExporting(true);
        try {
            const apiUrl = getApiUrl();
            const response = await fetch(`${apiUrl}/api/auto-reply/export`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json'
                }
            });

            if (!response.ok) {
                throw new Error('Error al exportar reglas');
            }

            const contentType = response.headers.get('content-type');

            if (contentType && contentType.includes('application/zip')) {
                // Download ZIP file
                const blob = await response.blob();
                const url = URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = `auto-reply-rules-${new Date().toISOString().split('T')[0]}.zip`;
                link.click();
                URL.revokeObjectURL(url);

                if (toast) {
                    toast.success('Reglas exportadas exitosamente');
                }
            } else {
                // Download JSON file (legacy, no media files)
                const data = await response.json();
                const dataStr = JSON.stringify(data.rules, null, 2);
                const dataBlob = new Blob([dataStr], { type: 'application/json' });
                const url = URL.createObjectURL(dataBlob);
                const link = document.createElement('a');
                link.href = url;
                link.download = `auto-reply-rules-${new Date().toISOString().split('T')[0]}.json`;
                link.click();
                URL.revokeObjectURL(url);

                if (toast) {
                    toast.success(`Se exportaron ${data.count} regla(s) exitosamente`);
                }
            }
        } catch (error: any) {
            console.error('Error exporting rules:', error);
            if (toast) {
                toast.error('Error al exportar reglas: ' + error.message);
            }
        } finally {
            setIsExporting(false);
        }
    };

    // Import rules from JSON file
    const handleImportRules = async (file: File) => {
        try {
            const reader = new FileReader();
            reader.onload = async (event) => {
                try {
                    const text = event.target?.result as string;
                    const importedRules = JSON.parse(text);

                    if (!Array.isArray(importedRules)) {
                        if (toast) {
                            toast.error('El archivo debe contener un array de reglas');
                        }
                        return;
                    }

                    // Use the import endpoint which handles media files automatically
                    try {
                        const response = await importAutoReplyRules(importedRules);

                        if (response.success && response.imported > 0) {
                            // Reload rules to get the imported ones
                            const rulesResponse = await getAutoReplyRules();
                            if (rulesResponse.success) {
                                setRules(rulesResponse.rules);
                            }

                            let message = `Se importaron ${response.imported} regla(s) exitosamente`;
                            if (response.failed > 0) {
                                message += `. ${response.failed} regla(s) no pudieron importarse.`;
                            }
                            if (response.errors && response.errors.length > 0) {
                                const errorPreview = response.errors.slice(0, 3).join('; ');
                                message += ` Errores: ${errorPreview}${response.errors.length > 3 ? '...' : ''}`;
                            }

                            if (toast) {
                                toast.success(message);
                            }
                        } else {
                            if (toast) {
                                const errorMsg = response.errors && response.errors.length > 0
                                    ? response.errors.slice(0, 3).join('; ')
                                    : 'No se pudieron importar las reglas';
                                toast.error(errorMsg);
                            }
                        }
                    } catch (error: any) {
                        console.error('Error importing rules:', error);
                        if (toast) {
                            toast.error('Error al importar reglas: ' + (error.response?.data?.error || error.message));
                        }
                    }
                } catch (error: any) {
                    console.error('Error parsing imported file:', error);
                    if (toast) {
                        toast.error('Error al procesar el archivo: ' + error.message);
                    }
                }
            };
            reader.readAsText(file);
        } catch (error: any) {
            console.error('Error importing rules:', error);
            if (toast) {
                toast.error('Error al importar reglas: ' + error.message);
            }
        }
    };

    const handleImportFromModal = async (file: File, applyToAllSessions: boolean) => {
        const ext = file.name.toLowerCase().split('.').pop();
        if (ext !== 'json' && ext !== 'zip') {
            if (toast) {
                toast.error('Por favor selecciona un archivo JSON o ZIP válido');
            }
            return;
        }

        try {
            const formData = new FormData();
            formData.append('file', file);
            formData.append('applyToAllSessions', String(applyToAllSessions));

            const apiUrl = getApiUrl();
            const response = await fetch(`${apiUrl}/api/auto-reply/rules/import`, {
                method: 'POST',
                body: formData
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Error al importar');
            }

            const result = await response.json();

            if (result.success && result.success > 0) {
                // Reload rules
                const rulesResponse = await getAutoReplyRules();
                if (rulesResponse.success) {
                    setRules(rulesResponse.rules);
                }

                const parts = [];
                if (result.success > 0) parts.push(`${result.success} nueva(s)`);
                if (result.replaced > 0) parts.push(`${result.replaced} reemplazada(s)`);
                if (result.failed > 0) parts.push(`${result.failed} fallida(s)`);

                const sessionScope = applyToAllSessions ? ' (todas las sesiones)' : ' (sesión activa)';
                const message = parts.length > 0
                    ? `Reglas importadas: ${parts.join(', ')}${sessionScope}`
                    : `Importación completada${sessionScope}`;

                if (toast) {
                    toast.success(message);
                }

                if (result.errors && result.errors.length > 0) {
                    console.warn('Import errors:', result.errors);
                }
            } else {
                if (toast) {
                    toast.error('No se pudieron importar las reglas');
                }
            }
        } catch (error: any) {
            console.error('Error importing rules:', error);
            if (toast) {
                toast.error('Error al importar reglas: ' + error.message);
            }
        }
    };

    const handleImportFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        const ext = file.name.toLowerCase().split('.').pop();
        if (ext !== 'json' && ext !== 'zip') {
            if (toast) {
                toast.error('Por favor selecciona un archivo JSON o ZIP válido');
            }
            return;
        }

        try {
            const formData = new FormData();
            formData.append('file', file);

            const apiUrl = getApiUrl();
            const response = await fetch(`${apiUrl}/api/auto-reply/rules/import`, {
                method: 'POST',
                body: formData
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Error al importar');
            }

            const result = await response.json();

            if (result.success && result.success > 0) {
                // Reload rules
                const rulesResponse = await getAutoReplyRules();
                if (rulesResponse.success) {
                    setRules(rulesResponse.rules);
                }

                const parts = [];
                if (result.success > 0) parts.push(`${result.success} nueva(s)`);
                if (result.replaced > 0) parts.push(`${result.replaced} reemplazada(s)`);
                if (result.failed > 0) parts.push(`${result.failed} fallida(s)`);

                const message = parts.length > 0
                    ? `Reglas importadas: ${parts.join(', ')}`
                    : 'Importación completada';

                if (toast) {
                    toast.success(message);
                }

                if (result.errors && result.errors.length > 0) {
                    console.warn('Import errors:', result.errors);
                }
            } else {
                if (toast) {
                    toast.error('No se pudieron importar las reglas');
                }
            }
        } catch (error: any) {
            console.error('Error importing rules:', error);
            if (toast) {
                toast.error('Error al importar reglas: ' + error.message);
            }
        } finally {
            // Reset input
            if (importInputRef.current) {
                importInputRef.current.value = '';
            }
        }
    };

    return (
        <>
            {/* Media Upload Progress Bar */}
            {(media.uploadProgress || kbMedia.uploadProgress || flyerMedia.uploadProgress) && (
                <BulkProgressBar
                    current={media.uploadProgress?.current || kbMedia.uploadProgress?.current || flyerMedia.uploadProgress?.current || 0}
                    total={media.uploadProgress?.total || kbMedia.uploadProgress?.total || flyerMedia.uploadProgress?.total || 0}
                    isActive={true}
                    title="Subiendo y optimizando archivos..."
                    subtitle="Procesando archivos adjuntos (compresión WebP local activa)"
                />
            )}

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 h-[calc(100vh-8rem)]">

                {/* Left Column: Rules List */}
                <div className={`${showMobileEditor ? 'hidden lg:flex' : 'flex'} lg:col-span-1 flex-col gap-4 h-full overflow-hidden`}>
                    <div className="bg-theme-card p-6 rounded-xl shadow-sm border border-theme">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="font-semibold text-theme-main flex items-center gap-2">
                                <Bot size={20} className="text-primary-600" /> Reglas Activas
                            </h3>
                            <button
                                onClick={() => setShowMobileEditor(true)}
                                className="lg:hidden bg-primary-600 text-white p-2 rounded-lg"
                                title="Nueva Regla"
                            >
                                <Plus size={20} />
                            </button>
                        </div>
                        <div className="mb-4">
                            <GlobalSessionIndicator enabled={globalSessionsEnabled} />
                            <p className="text-xs text-theme-muted mt-2">
                                El bot responderá automáticamente cuando detecte estas palabras clave.
                            </p>
                        </div>

                        {/* Export/Import/Bulk buttons */}
                        <div className="flex flex-col gap-2">
                            <button
                                onClick={() => setShowBulkCreator(true)}
                                className="w-full flex items-center justify-center gap-2 px-3 py-2.5 bg-gradient-to-r from-primary-600 to-blue-600 text-white rounded-lg hover:from-primary-700 hover:to-blue-700 transition-all text-sm font-semibold shadow-sm"
                                title="Crear reglas para múltiples países a la vez"
                            >
                                <Zap size={16} />
                                Creación Masiva por País
                            </button>
                            <div className="flex gap-2">
                                <button
                                    onClick={handleExportRules}
                                    disabled={rules.length === 0 || isExporting}
                                    className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
                                    title="Exportar reglas a archivo JSON"
                                >
                                    {isExporting ? (
                                        <>
                                            <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                                            Exportando...
                                        </>
                                    ) : (
                                        <>
                                            <Download size={16} />
                                            Exportar
                                        </>
                                    )}
                                </button>
                                <button
                                    onClick={() => setShowImportModal(true)}
                                    className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
                                    title="Importar reglas desde archivo JSON"
                                >
                                    <Upload size={16} />
                                    Importar
                                </button>
                                <input
                                    ref={importInputRef}
                                    type="file"
                                    accept=".json,.zip"
                                    onChange={handleImportFile}
                                    className="hidden"
                                />
                            </div>
                        </div>
                    </div>

                    <div className="flex-1 bg-theme-card rounded-xl shadow-sm border border-theme overflow-hidden flex flex-col">
                        <div className="p-4 border-b border-theme bg-theme-base flex justify-between items-center">
                            <span className="font-medium text-theme-main text-sm">Lista de Reglas</span>
                            <span className="bg-slate-200 text-theme-muted px-2 py-0.5 rounded text-xs">{rules.length}</span>
                        </div>

                        <div className="flex-1 overflow-y-auto p-2 space-y-2 custom-scrollbar">
                            {rules.length === 0 ? (
                                <div className="text-center p-8 text-slate-400 italic text-sm">
                                    No hay reglas creadas.
                                </div>
                            ) : (
                                rules.map(rule => (
                                    <div id={`rule-container-${rule.id}`} key={rule.id} className={`p-4 rounded-lg border transition-all ${editingId === rule.id ? 'border-primary-500 bg-primary-50' : rule.isActive ? 'border-theme hover:border-primary-200 bg-theme-card' : 'border-theme bg-theme-base opacity-75'}`}>
                                        <div className="flex justify-between items-start gap-3 mb-2">
                                            <div className="flex items-center gap-2 flex-wrap pt-1">
                                                <h4 className="font-bold text-theme-main text-sm break-all">{rule.name}</h4>
                                                {rule.type === 'menu' && (
                                                    <span className="px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-700 rounded-full flex items-center gap-1 whitespace-nowrap">
                                                        <MenuIcon size={12} /> Menú
                                                    </span>
                                                )}
                                                {rule.type === 'ai' && (
                                                    <span className="px-2 py-0.5 text-xs font-medium bg-gradient-to-r from-purple-500 to-indigo-600 text-white rounded-full flex items-center gap-1 whitespace-nowrap animate-pulse">
                                                        <Bot size={12} /> Asistente IA
                                                    </span>
                                                )}
                                                {rule.type === 'ai' && rule.isOrchestrator && (
                                                    <span className="px-2 py-0.5 text-xs font-medium bg-gradient-to-r from-blue-500 to-teal-500 text-white rounded-full flex items-center gap-1 whitespace-nowrap">
                                                        🧠 Orquestador
                                                    </span>
                                                )}
                                            </div>
                                            <div className="flex flex-col items-end gap-2 shrink-0">
                                                {/* Primera fila: Toggle Activar/Desactivar */}
                                                <button
                                                    onClick={() => toggleStatus(rule.id)}
                                                    className={`px-3 py-1.5 rounded-lg transition-all flex items-center gap-1.5 ${rule.isActive
                                                        ? "text-primary-600 hover:bg-primary-50 bg-primary-50"
                                                        : "text-slate-400 hover:bg-slate-100 bg-theme-base"
                                                        }`}
                                                    title={rule.isActive ? "Desactivar regla" : "Activar regla"}
                                                >
                                                    <span className="text-[11px] font-medium hidden min-[400px]:inline">
                                                        {rule.isActive ? 'Activo' : 'Inactivo'}
                                                    </span>
                                                    {rule.isActive ? <ToggleRight size={18} /> : <ToggleLeft size={18} />}
                                                </button>
                                                {/* Segunda fila: Botonera Editar, Duplicar, Eliminar */}
                                                <div className="flex items-center gap-1">
                                                    <button
                                                        onClick={() => handleEdit(rule)}
                                                        className="p-1.5 rounded text-blue-500 hover:text-blue-700 hover:bg-blue-50 shadow-sm border border-transparent hover:border-blue-100 transition-all bg-theme-base"
                                                        title="Editar regla"
                                                    >
                                                        <Edit2 size={15} />
                                                    </button>
                                                    <button
                                                        onClick={() => handleDuplicate(rule)}
                                                        className="p-1.5 rounded text-slate-500 hover:text-slate-700 hover:bg-slate-50 shadow-sm border border-transparent hover:border-slate-200 transition-all bg-theme-base"
                                                        title="Duplicar regla"
                                                    >
                                                        <Copy size={15} />
                                                    </button>
                                                    <button
                                                        onClick={() => handleDelete(rule.id)}
                                                        className="p-1.5 rounded text-red-400 hover:text-red-600 hover:bg-red-50 shadow-sm border border-transparent hover:border-red-100 transition-all bg-theme-base"
                                                        title="Eliminar regla"
                                                    >
                                                        <Trash2 size={15} />
                                                    </button>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Country Filter Badge */}
                                        <div className="flex flex-wrap gap-1 mb-2">
                                            {/* Included Countries */}
                                            {rule.countries && rule.countries.length > 0 && (
                                                Array.from(new Set(rule.countries)).map(code => {
                                                    const country = countryList.find(c => c.code === code);
                                                    return (
                                                        <span key={`inc-${code}`} className="text-[10px] bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded flex items-center gap-1 border border-blue-100" title={`Incluido: ${country?.name}`}>
                                                            {country?.iso ? (
                                                                <img src={`https://flagcdn.com/w20/${country.iso.toLowerCase()}.png`} width="16" alt={country.name} className="rounded-sm" />
                                                            ) : <Globe size={10} />}
                                                            {country?.name || code}
                                                        </span>
                                                    );
                                                })
                                            )}

                                            {/* Excluded Countries */}
                                            {rule.excludeCountries && rule.excludeCountries.length > 0 && (
                                                Array.from(new Set(rule.excludeCountries)).map(code => {
                                                    const country = countryList.find(c => c.code === code);
                                                    return (
                                                        <span key={`exc-${code}`} className="text-[10px] bg-red-50 text-red-700 px-1.5 py-0.5 rounded flex items-center gap-1 border border-red-100" title={`Excluido: ${country?.name}`}>
                                                            <X size={10} className="text-red-400" />
                                                            {country?.iso ? (
                                                                <img src={`https://flagcdn.com/w20/${country.iso.toLowerCase()}.png`} width="16" alt={country.name} className="rounded-sm" />
                                                            ) : <Globe size={10} />}
                                                            {country?.name || code}
                                                        </span>
                                                    );
                                                })
                                            )}

                                            {(!rule.countries || rule.countries.length === 0) && (!rule.excludeCountries || rule.excludeCountries.length === 0) && (
                                                <span className="text-[10px] bg-slate-50 text-slate-500 px-1.5 py-0.5 rounded flex items-center gap-1 border border-theme">
                                                    <Globe size={10} /> Todos los países
                                                </span>
                                            )}
                                        </div>

                                        <div className="flex flex-wrap gap-1 mb-2">
                                            {(() => {
                                                // Normalize keywords to array (handle string or array)
                                                let keywordsArray: string[] = [];
                                                if (Array.isArray(rule.keywords)) {
                                                    keywordsArray = rule.keywords;
                                                } else if (typeof rule.keywords === 'string') {
                                                    try {
                                                        // Try to parse as JSON string
                                                        keywordsArray = JSON.parse(rule.keywords);
                                                    } catch {
                                                        // If not JSON, split by comma
                                                        keywordsArray = rule.keywords.split(',').map((k: string) => k.trim()).filter((k: string) => k.length > 0);
                                                    }
                                                }
                                                return keywordsArray.map((k, i) => (
                                                    <span key={i} className="text-[10px] bg-slate-100 text-theme-muted px-1.5 py-0.5 rounded border border-theme">
                                                        {k}
                                                    </span>
                                                ));
                                            })()}
                                        </div>

                                        {/* Media Thumbnail */}
                                        {rule.mediaPath && (() => {
                                            return (
                                                <div className="mb-2">
                                                    <MediaThumbnail
                                                        src={getMediaPreviewUrl(rule.mediaPath)}
                                                        mediaPath={rule.mediaPath}
                                                        type={getMediaTypeFromPath(rule.mediaPath)}
                                                        caption={rule.caption}
                                                        className="h-20"
                                                    />
                                                </div>
                                            );
                                        })()}

                                        <p className="text-xs text-theme-muted line-clamp-2 italic border-l-2 border-theme pl-2">
                                            {rule.type === 'ai' ? (rule.isOrchestrator ? '🧠 Orquestador: Delegación automática a agentes expertos' : `IA Prompt: "${rule.systemPrompt}"`) : `"${rule.response}"`}
                                        </p>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                </div>

                {/* Right Column: Editor */}
                <div className={`${showMobileEditor ? 'block' : 'hidden lg:block'} lg:col-span-2 h-full overflow-hidden`}>
                    <div className="bg-theme-card p-6 rounded-xl shadow-sm border border-theme h-full flex flex-col overflow-hidden">
                        <div className="flex justify-between items-center mb-6">
                            <h3 className="font-semibold text-theme-main flex items-center gap-2">
                                {editingId ? <><Edit2 size={18} /> Editar Regla</> : <><Plus size={18} /> Nueva Regla</>}
                            </h3>
                            {editingId ? (
                                <button
                                    onClick={resetForm}
                                    className="text-xs text-theme-muted flex items-center gap-1 hover:text-red-500 px-2 py-1 rounded-lg hover:bg-theme-base transition-colors"
                                    title="Cancelar Edición"
                                >
                                    <X size={14} /> Cancelar Edición
                                </button>
                            ) : showMobileEditor ? (
                                <button
                                    onClick={() => setShowMobileEditor(false)}
                                    className="lg:hidden p-1.5 text-theme-muted hover:text-theme-main rounded-lg hover:bg-theme-base"
                                    title="Cerrar"
                                >
                                    <X size={18} />
                                </button>
                            ) : null}
                        </div>

                        {formErrors.general && (
                            <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
                                <p className="text-sm text-red-600">{formErrors.general}</p>
                            </div>
                        )}

                        <div className="space-y-4 flex-1 overflow-y-auto custom-scrollbar pr-2">
                            <div>
                                <label className="block text-sm font-medium text-theme-main mb-1">Nombre de la Regla</label>
                                <input
                                    type="text"
                                    className={`w-full border rounded-lg px-4 py-2 text-sm focus:ring-primary-500 focus:border-primary-500 ${formErrors.name ? 'border-red-300' : 'border-theme'
                                        }`}
                                    placeholder="Ej: Respuesta Saludo"
                                    value={formData.name}
                                    onChange={e => {
                                        setFormData({ ...formData, name: e.target.value });
                                        if (formErrors.name) setFormErrors({ ...formErrors, name: '' });
                                    }}
                                />
                                {formErrors.name && (
                                    <p className="mt-1 text-sm text-red-600">{formErrors.name}</p>
                                )}
                            </div>

                            {/* Type Selector */}
                            <div>
                                <label className="block text-sm font-medium text-theme-main mb-1">Tipo de Respuesta</label>
                                <select
                                    className="w-full border border-theme rounded-lg px-4 py-2 text-sm focus:ring-primary-500 focus:border-primary-500 bg-theme-card font-semibold"
                                    value={formData.type || 'simple'}
                                    onChange={e => {
                                        const newType = e.target.value as 'simple' | 'menu' | 'ai';
                                        setFormData({
                                            ...formData,
                                            type: newType,
                                            menuId: newType === 'menu' ? formData.menuId : undefined,
                                            aiProvider: newType === 'ai' ? (formData.aiProvider || 'google') : undefined,
                                            aiModel: newType === 'ai' ? (formData.aiModel || 'auto') : undefined,
                                            systemPrompt: newType === 'ai' ? (formData.systemPrompt || 'Eres un asistente virtual de atención al cliente útil y educado.') : undefined
                                        });
                                    }}
                                >
                                    <option value="simple">💬 Respuesta Simple</option>
                                    <option value="menu">🎯 Menú Interactivo</option>
                                    <option value="ai">🤖 Asistente de IA (Multimodal + Failover)</option>
                                </select>
                                <p className="text-xs text-slate-400 mt-1">
                                    {formData.type === 'menu' && 'Inicia una conversación guiada con opciones.'}
                                    {formData.type === 'simple' && 'Envía un mensaje de respuesta directa.'}
                                    {formData.type === 'ai' && 'Conversación guiada inteligente con memoria y soporte para fotos/audios.'}
                                </p>
                            </div>

                            {/* Menu Selector (only if type is 'menu') */}
                            {formData.type === 'menu' && (
                                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                                    <label className="block text-sm font-medium text-theme-main mb-2 flex items-center gap-2">
                                        <MenuIcon size={16} className="text-blue-600" />
                                        Seleccionar Menú
                                    </label>
                                    {menus.length === 0 ? (
                                        <div className="text-sm text-theme-muted bg-theme-card rounded-lg p-4 border border-theme">
                                            <p className="mb-2">⚠️ No hay menús activos disponibles.</p>
                                            <p className="text-xs text-theme-muted">
                                                Ve a <strong>Menús Interactivos</strong> para crear un menú primero.
                                            </p>
                                        </div>
                                    ) : (
                                        <>
                                            <select
                                                className={`w-full border rounded-lg px-4 py-2 text-sm focus:ring-blue-500 focus:border-blue-500 bg-theme-card ${formErrors.menuId ? 'border-red-300' : 'border-theme'
                                                    }`}
                                                value={formData.menuId || ''}
                                                onChange={e => {
                                                    setFormData({ ...formData, menuId: e.target.value });
                                                    if (formErrors.menuId) setFormErrors({ ...formErrors, menuId: '' });
                                                }}
                                            >
                                                <option value="">-- Selecciona un menú --</option>
                                                {menus.map(menu => (
                                                    <option key={menu.id} value={menu.id}>
                                                        {menu.name} ({menu.options.length} opciones)
                                                    </option>
                                                ))}
                                            </select>
                                            {formErrors.menuId && (
                                                <p className="mt-1 text-sm text-red-600">{formErrors.menuId}</p>
                                            )}
                                            {formData.menuId && (
                                                <div className="mt-3 text-xs text-theme-muted bg-theme-card rounded p-3 border border-theme">
                                                    <p className="font-medium mb-1">📋 Preview del menú:</p>
                                                    <p className="whitespace-pre-wrap">
                                                        {menus.find(m => m.id === formData.menuId)?.message}
                                                    </p>
                                                </div>
                                            )}
                                        </>
                                    )}
                                </div>
                            )}

                            {/* Country Selector */}
                            <div className="bg-theme-base border border-theme rounded-lg p-3">
                                <button
                                    onClick={() => setShowCountrySelector(!showCountrySelector)}
                                    className="flex items-center justify-between w-full text-sm font-medium text-theme-main"
                                >
                                    <span className="flex items-center gap-2">
                                        <Globe size={16} className="text-primary-600" />
                                        Filtros Geográficos (opcional)
                                    </span>
                                    {showCountrySelector ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                                </button>

                                {showCountrySelector && (
                                    <div className="mt-3 space-y-4">
                                        {/* Tabs */}
                                        <div className="flex bg-theme-card p-1 rounded-lg border border-theme">
                                            <button
                                                type="button"
                                                onClick={() => setCountryTab('include')}
                                                className={`flex-1 py-1.5 text-xs font-medium rounded-md transition-all ${countryTab === 'include'
                                                    ? 'bg-primary-600 text-white shadow-sm'
                                                    : 'text-theme-muted hover:text-theme-main'
                                                    }`}
                                            >
                                                Incluir
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => setCountryTab('exclude')}
                                                className={`flex-1 py-1.5 text-xs font-medium rounded-md transition-all ${countryTab === 'exclude'
                                                    ? 'bg-red-600 text-white shadow-sm'
                                                    : 'text-theme-muted hover:text-theme-main'
                                                    }`}
                                            >
                                                Excluir
                                            </button>
                                        </div>

                                        <div className="relative">
                                            <input
                                                type="text"
                                                placeholder="Buscar país o prefijo..."
                                                className={`w-full bg-theme-card border border-theme rounded-md py-2 px-3 pl-9 text-sm focus:outline-none focus:ring-1 transition-all ${countryTab === 'include' ? 'focus:ring-primary-500' : 'focus:ring-red-500'
                                                    }`}
                                                value={countrySearch}
                                                onChange={(e) => setCountrySearch(e.target.value)}
                                            />
                                            <Globe size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-theme-muted" />
                                        </div>

                                        <div>
                                            <div className="flex items-center justify-between px-1 mb-2">
                                                <p className="text-[10px] text-theme-muted">
                                                    {countryTab === 'include'
                                                        ? 'Selecciona los países para los cuales esta regla DEBE activarse.'
                                                        : 'Selecciona los países a los que NO se les debe responder.'}
                                                </p>
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        const field = countryTab === 'include' ? 'countries' : 'excludeCountries';
                                                        const filteredList = countryList.filter(country =>
                                                            country.name.toLowerCase().includes(countrySearch.toLowerCase()) ||
                                                            country.code.includes(countrySearch.replace('+', ''))
                                                        );
                                                        const filteredCodes = filteredList.map(c => c.code);
                                                        const currentValues = formData[field] || [];
                                                        const allSelected = filteredCodes.every(code => currentValues.includes(code));

                                                        if (allSelected) {
                                                            setFormData({ ...formData, [field]: currentValues.filter(code => !filteredCodes.includes(code)) });
                                                        } else {
                                                            setFormData({ ...formData, [field]: Array.from(new Set([...currentValues, ...filteredCodes])) });
                                                        }
                                                    }}
                                                    className={`text-[10px] font-medium hover:underline ${countryTab === 'include' ? 'text-primary-600' : 'text-red-600'
                                                        }`}
                                                >
                                                    {(() => {
                                                        const field = countryTab === 'include' ? 'countries' : 'excludeCountries';
                                                        const filteredCodes = countryList.filter(country =>
                                                            country.name.toLowerCase().includes(countrySearch.toLowerCase()) ||
                                                            country.code.includes(countrySearch.replace('+', ''))
                                                        ).map(c => c.code);
                                                        const currentValues = formData[field] || [];
                                                        return filteredCodes.every(code => currentValues.includes(code)) ? 'Desmarcar todo' : 'Marcar todo';
                                                    })()}
                                                </button>
                                            </div>

                                            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 max-h-48 overflow-y-auto p-1 border border-theme rounded-md bg-theme-card/30">
                                                {countryTab === 'include' && ('otros paises'.includes(countrySearch.toLowerCase()) || 'other'.includes(countrySearch.toLowerCase())) && (() => {
                                                    const isChecked = (formData.countries || []).includes('other');
                                                    return (
                                                        <label
                                                            key="country-tab-virtual-other"
                                                            className={`flex items-center gap-2 p-2 rounded cursor-pointer border border-transparent transition-colors ${isChecked
                                                                ? 'bg-primary-50/50 border-primary-200'
                                                                : 'hover:bg-theme-card'
                                                                }`}
                                                        >
                                                            <input
                                                                type="checkbox"
                                                                className="rounded text-primary-600"
                                                                checked={isChecked}
                                                                onChange={(e) => {
                                                                    const currentValues = formData.countries || [];
                                                                    if (e.target.checked) {
                                                                        setFormData({ ...formData, countries: [...currentValues, 'other'] });
                                                                    } else {
                                                                        setFormData({ ...formData, countries: currentValues.filter(c => c !== 'other') });
                                                                    }
                                                                }}
                                                            />
                                                            <span className="text-[10px] flex items-center gap-2">
                                                                <Globe size={14} className="text-primary-600 shrink-0" />
                                                                <span className="truncate font-semibold text-primary-700 dark:text-primary-400">Otros países (Resto)</span>
                                                            </span>
                                                        </label>
                                                    );
                                                })()}
                                                {countryList
                                                    .filter(country =>
                                                        country.name.toLowerCase().includes(countrySearch.toLowerCase()) ||
                                                        country.code.includes(countrySearch.replace('+', ''))
                                                    )
                                                    .map(country => {
                                                        const field = countryTab === 'include' ? 'countries' : 'excludeCountries';
                                                        const isChecked = (formData[field] || []).includes(country.code);
                                                        return (
                                                            <label
                                                                key={`${countryTab}-${country.iso}-${country.code}`}
                                                                className={`flex items-center gap-2 p-2 rounded cursor-pointer border border-transparent transition-colors ${isChecked
                                                                    ? (countryTab === 'include' ? 'bg-primary-50/50 border-primary-200' : 'bg-red-50/50 border-red-200')
                                                                    : 'hover:bg-theme-card'
                                                                    }`}
                                                            >
                                                                <input
                                                                    type="checkbox"
                                                                    className={`rounded ${countryTab === 'include' ? 'text-primary-600' : 'text-red-600'}`}
                                                                    checked={isChecked}
                                                                    onChange={(e) => {
                                                                        const currentValues = formData[field] || [];
                                                                        if (e.target.checked) {
                                                                            setFormData({ ...formData, [field]: [...currentValues, country.code] });
                                                                        } else {
                                                                            setFormData({ ...formData, [field]: currentValues.filter(c => c !== country.code) });
                                                                        }
                                                                    }}
                                                                />
                                                                <span className="text-[10px] flex items-center gap-2">
                                                                    {country.iso && (
                                                                        <img
                                                                            src={`https://flagcdn.com/w20/${country.iso.toLowerCase()}.png`}
                                                                            width="16"
                                                                            alt={country.name}
                                                                            className="rounded-sm shadow-sm"
                                                                        />
                                                                    )}
                                                                    <span className="truncate">{country.name} (+{country.code})</span>
                                                                </span>
                                                            </label>
                                                        );
                                                    })}
                                            </div>
                                        </div>

                                        {/* Selected summary for both tabs */}
                                        <div className="space-y-2 pt-2 border-t border-theme">
                                            {/* Included summary */}
                                            {formData.countries && formData.countries.length > 0 && (
                                                <div className="flex flex-wrap gap-1 items-center">
                                                    <span className="text-[10px] font-semibold text-primary-600 mr-1">Incluidos:</span>
                                                    {formData.countries.map(code => {
                                                        if (code === 'other') {
                                                            return (
                                                                <span key="inc-other" className="bg-primary-50 text-primary-700 text-[10px] px-1.5 py-0.5 rounded-full flex items-center gap-1 border border-primary-100">
                                                                    <Globe size={10} className="text-primary-600" />
                                                                    <span>Otros países</span>
                                                                    <button onClick={(e) => { e.preventDefault(); setFormData({ ...formData, countries: formData.countries?.filter(c => c !== 'other') }); }} className="hover:text-primary-900"><X size={8} /></button>
                                                                </span>
                                                            );
                                                        }
                                                        const country = countryList.find(c => c.code === code);
                                                        return (
                                                            <span key={`inc-${code}`} className="bg-primary-50 text-primary-700 text-[10px] px-1.5 py-0.5 rounded-full flex items-center gap-1 border border-primary-100">
                                                                {country?.iso && <img src={`https://flagcdn.com/w20/${country.iso.toLowerCase()}.png`} width="12" alt="" className="rounded-xs" />}
                                                                {country?.name || code}
                                                                <button onClick={(e) => { e.preventDefault(); setFormData({ ...formData, countries: formData.countries?.filter(c => c !== code) }); }} className="hover:text-primary-900"><X size={8} /></button>
                                                            </span>
                                                        );
                                                    })}
                                                </div>
                                            )}
                                            {/* Excluded summary */}
                                            {formData.excludeCountries && formData.excludeCountries.length > 0 && (
                                                <div className="flex flex-wrap gap-1 items-center">
                                                    <span className="text-[10px] font-semibold text-red-600 mr-1">Excluidos:</span>
                                                    {formData.excludeCountries.map(code => {
                                                        const country = countryList.find(c => c.code === code);
                                                        return (
                                                            <span key={`exc-${code}`} className="bg-red-50 text-red-700 text-[10px] px-1.5 py-0.5 rounded-full flex items-center gap-1 border border-red-100">
                                                                {country?.iso && <img src={`https://flagcdn.com/w20/${country.iso.toLowerCase()}.png`} width="12" alt="" className="rounded-xs" />}
                                                                {country?.name || code}
                                                                <button onClick={(e) => { e.preventDefault(); setFormData({ ...formData, excludeCountries: formData.excludeCountries?.filter(c => c !== code) }); }} className="hover:text-red-900"><X size={8} /></button>
                                                            </span>
                                                        );
                                                    })}
                                                </div>
                                            )}
                                        </div>

                                        <div className="pt-2"> {/* Removed extra border-t as previous block has it */}
                                            <label className="flex items-center gap-2 cursor-pointer text-xs group">
                                                <input
                                                    type="checkbox"
                                                    checked={formData.allowUnknownCountries || false}
                                                    onChange={(e) => setFormData({ ...formData, allowUnknownCountries: e.target.checked })}
                                                    className="h-3 w-3 text-primary-600 rounded border-theme focus:ring-primary-500"
                                                />
                                                <span className="text-theme-muted group-hover:text-theme-main transition-colors">
                                                    Permitir si el país no es detectable (LID desconocido)
                                                </span>
                                            </label>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* RESPUESTAS ESPECÍFICAS POR PAÍS */}
                            {formData.type !== 'menu' && formData.countries && formData.countries.length > 0 && (
                                <div className="bg-slate-50/50 dark:bg-slate-900/30 border border-theme rounded-lg p-4 transition-all duration-300">
                                    <div className="flex items-center justify-between border-b border-theme/50 pb-3">
                                        <button
                                            type="button"
                                            onClick={() => setIsCountryResponsesExpanded(!isCountryResponsesExpanded)}
                                            className="flex items-center gap-2 text-sm font-bold text-theme-main hover:text-primary-600 transition-colors focus:outline-none"
                                        >
                                            <Bot size={16} className="text-primary-600 shrink-0" />
                                            <span>Respuestas Personalizadas por País</span>
                                            <span className="text-xs font-normal text-theme-muted bg-theme-base border border-theme px-2 py-0.5 rounded-full shrink-0">
                                                {formData.countries.length} {formData.countries.length === 1 ? 'país' : 'países'}
                                            </span>
                                            {isCountryResponsesExpanded ? <ChevronUp size={16} className="text-theme-muted shrink-0" /> : <ChevronDown size={16} className="text-theme-muted shrink-0" />}
                                        </button>

                                        {isCountryResponsesExpanded && (
                                            <div className="flex items-center bg-theme-base border border-theme rounded-lg p-0.5 shrink-0 shadow-sm">
                                                <button
                                                    type="button"
                                                    onClick={() => setCountryResponsesLayout('1col')}
                                                    className={`text-xs px-2.5 py-1 rounded-md font-medium transition-all ${countryResponsesLayout === '1col'
                                                        ? 'bg-primary-600 text-white shadow-sm'
                                                        : 'text-theme-muted hover:text-theme-main hover:bg-slate-100 dark:hover:bg-slate-800'
                                                        }`}
                                                >
                                                    1 Columna
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => setCountryResponsesLayout('2col')}
                                                    className={`text-xs px-2.5 py-1 rounded-md font-medium transition-all ${countryResponsesLayout === '2col'
                                                        ? 'bg-primary-600 text-white shadow-sm'
                                                        : 'text-theme-muted hover:text-theme-main hover:bg-slate-100 dark:hover:bg-slate-800'
                                                        }`}
                                                >
                                                    2 Columnas
                                                </button>
                                            </div>
                                        )}
                                    </div>

                                    {isCountryResponsesExpanded && (
                                        <div className="space-y-4 pt-3">
                                            <p className="text-xs text-theme-muted leading-relaxed">
                                                Configura archivos multimedia y captions específicos para cada uno de los países seleccionados. Si no los personalizas, recibirán el comportamiento por defecto configurado abajo.
                                            </p>

                                            <div className={`grid gap-4 ${countryResponsesLayout === '2col' ? 'grid-cols-1 md:grid-cols-2' : 'grid-cols-1'}`}>
                                                {formData.countries.map(code => {
                                                    const isOther = code === 'other';
                                                    const country = isOther ? null : countryList.find(c => c.code === code);
                                                    const hasOverride = !!formData.countryResponses?.[code];
                                                    const overrideData = formData.countryResponses?.[code] || { response: '', mediaPaths: [], captions: [] };

                                                    return (
                                                        <div key={`override-${code}`} className="border border-theme rounded-lg bg-theme-card overflow-hidden transition-all duration-200 hover:shadow-sm">
                                                            <div className="p-3 bg-theme-base flex items-center justify-between">
                                                                <div className="flex items-center gap-2 text-xs font-semibold text-theme-main">
                                                                    {isOther ? (
                                                                        <Globe size={16} className="text-primary-600 shrink-0" />
                                                                    ) : (
                                                                        country?.iso && (
                                                                            <img
                                                                                src={`https://flagcdn.com/w20/${country.iso.toLowerCase()}.png`}
                                                                                width="18"
                                                                                alt=""
                                                                                className="rounded-sm shadow-sm"
                                                                            />
                                                                        )
                                                                    )}
                                                                    <span>{isOther ? 'Otros países (Resto)' : (country?.name || `Prefijo +${code}`)}</span>
                                                                </div>
                                                                <button
                                                                    type="button"
                                                                    onClick={() => {
                                                                        const updatedResponses = { ...(formData.countryResponses || {}) };
                                                                        if (hasOverride) {
                                                                            delete updatedResponses[code];
                                                                        } else {
                                                                            updatedResponses[code] = { response: '', mediaPaths: [], captions: [] };
                                                                        }
                                                                        setFormData({ ...formData, countryResponses: updatedResponses });
                                                                    }}
                                                                    className={`text-xs font-semibold px-2.5 py-1 rounded transition-colors ${hasOverride
                                                                        ? 'bg-red-50 text-red-600 hover:bg-red-100 dark:bg-red-950/30 dark:text-red-400 dark:hover:bg-red-950/60'
                                                                        : 'bg-primary-50 text-primary-600 hover:bg-primary-100 dark:bg-primary-950/30 dark:text-primary-400 dark:hover:bg-primary-950/60'
                                                                        }`}
                                                                >
                                                                    {hasOverride ? 'Quitar Personalización' : 'Personalizar'}
                                                                </button>
                                                            </div>

                                                            {hasOverride && (
                                                                <div className="p-3 space-y-3 border-t border-theme">
                                                                    {/* Country Media Specific */}
                                                                    <div className="space-y-2">
                                                                        <label className="block text-[11px] font-medium text-theme-main">Archivos Multimedia Exclusivos (Opcional)</label>

                                                                        {/* Files Preview */}
                                                                        {overrideData.mediaPaths && overrideData.mediaPaths.length > 0 && (
                                                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-2">
                                                                                {overrideData.mediaPaths.map((mp, index) => {
                                                                                    const fileName = mp.split(/[/\\]/).pop() || 'archivo';
                                                                                    const type = getMediaTypeFromPath(mp);
                                                                                    const previewUrl = getMediaPreviewUrl(mp);

                                                                                    return (
                                                                                        <div key={index} className="border border-theme rounded p-2 bg-theme-base relative group space-y-2">
                                                                                            <div className="flex items-center justify-between">
                                                                                                <MediaThumbnail
                                                                                                    src={previewUrl}
                                                                                                    mediaPath={mp}
                                                                                                    type={type}
                                                                                                    className="h-12 w-12 shrink-0 rounded-lg border border-theme"
                                                                                                />
                                                                                            </div>
                                                                                            <div className="w-full">
                                                                                                <textarea
                                                                                                    ref={el => { countryCaptionRefs.current[`${code}-${index}`] = el; }}
                                                                                                    placeholder="Caption (subtítulo)..."
                                                                                                    className="w-full border border-theme rounded-lg px-2.5 py-1.5 text-sm bg-theme-card focus:ring-1 focus:ring-primary-500 min-h-[36px] max-h-[120px] resize-none overflow-y-auto font-sans"
                                                                                                    rows={1}
                                                                                                    value={overrideData.captions?.[index] || ''}
                                                                                                    onChange={e => {
                                                                                                        const updatedResponses = { ...(formData.countryResponses || {}) };
                                                                                                        const nextCaptions = [...(overrideData.captions || [])];
                                                                                                        nextCaptions[index] = e.target.value;
                                                                                                        updatedResponses[code] = {
                                                                                                            ...overrideData,
                                                                                                            captions: nextCaptions
                                                                                                        };
                                                                                                        setFormData({ ...formData, countryResponses: updatedResponses });
                                                                                                    }}
                                                                                                />
                                                                                            </div>
                                                                                            <button
                                                                                                type="button"
                                                                                                onClick={() => {
                                                                                                    const updatedResponses = { ...(formData.countryResponses || {}) };
                                                                                                    const nextPaths = overrideData.mediaPaths?.filter((_, idx) => idx !== index) || [];
                                                                                                    const nextCaptions = overrideData.captions?.filter((_, idx) => idx !== index) || [];
                                                                                                    updatedResponses[code] = {
                                                                                                        ...overrideData,
                                                                                                        mediaPaths: nextPaths,
                                                                                                        captions: nextCaptions
                                                                                                    };
                                                                                                    setFormData({ ...formData, countryResponses: updatedResponses });
                                                                                                }}
                                                                                                className="absolute -top-1.5 -right-1.5 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 opacity-0 group-hover:opacity-100 transition-opacity shadow-sm"
                                                                                            >
                                                                                                <X size={10} />
                                                                                            </button>
                                                                                        </div>
                                                                                    );
                                                                                })}
                                                                            </div>
                                                                        )}

                                                                        {/* Country file upload button */}
                                                                        <div>
                                                                            <label className="flex items-center justify-center gap-1.5 border border-dashed border-theme hover:border-primary-500 rounded-lg p-3 bg-theme-base cursor-pointer hover:bg-primary-50/20 transition-all text-xs font-semibold text-theme-muted hover:text-primary-600">
                                                                                {isLoadingCountryMedia[code] ? (
                                                                                    <>
                                                                                        <Loader size={14} className="animate-spin text-primary-600" />
                                                                                        <span>Subiendo archivos...</span>
                                                                                    </>
                                                                                ) : (
                                                                                    <>
                                                                                        <Paperclip size={14} />
                                                                                        <span>Adjuntar multimedia para {isOther ? 'Otros países' : (country?.name || code)}</span>
                                                                                    </>
                                                                                )}
                                                                                <input
                                                                                    type="file"
                                                                                    multiple
                                                                                    accept="image/*,video/*,application/*"
                                                                                    className="hidden"
                                                                                    disabled={isLoadingCountryMedia[code]}
                                                                                    onChange={e => handleCountryMediaSelect(e, code)}
                                                                                />
                                                                            </label>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}

                            <div>
                                <label className="block text-sm font-medium text-theme-main mb-1">Palabras Clave (Triggers)</label>
                                <input
                                    type="text"
                                    className={`w-full border rounded-lg px-4 py-2 text-sm focus:ring-primary-500 focus:border-primary-500 ${formErrors.keywords ? 'border-red-300' : 'border-theme'
                                        }`}
                                    placeholder="hola, buenos dias, info, precio (separadas por coma)"
                                    value={keywordInput}
                                    onChange={e => {
                                        setKeywordInput(e.target.value);
                                        if (formErrors.keywords) setFormErrors({ ...formErrors, keywords: '' });
                                    }}
                                />
                                {formErrors.keywords ? (
                                    <p className="mt-1 text-sm text-red-600">{formErrors.keywords}</p>
                                ) : (
                                    <p className="text-xs text-slate-400 mt-1">El bot responderá si el mensaje recibido contiene alguna de estas palabras.</p>
                                )}
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-theme-main mb-1">Tipo de Coincidencia</label>
                                    <select
                                        className="w-full border border-theme rounded-lg px-4 py-2 text-sm focus:ring-primary-500 focus:border-primary-500 bg-theme-card"
                                        value={formData.matchType}
                                        onChange={e => setFormData({ ...formData, matchType: e.target.value as any })}
                                    >
                                        <option value="contains">Contiene (Flexible)</option>
                                        <option value="exact">Exacta (Estricta)</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-theme-main mb-1 flex items-center gap-2">
                                        <Clock size={14} /> Retraso de Respuesta (seg)
                                    </label>
                                    <input
                                        type="number"
                                        min="0"
                                        max="60"
                                        className="w-full border border-theme rounded-lg px-4 py-2 text-sm focus:ring-primary-500 focus:border-primary-500"
                                        value={formData.delay}
                                        onChange={e => setFormData({ ...formData, delay: parseInt(e.target.value) })}
                                    />
                                </div>
                            </div>

                            {/* Response (only for simple type) */}
                            {formData.type !== 'menu' && formData.type !== 'ai' && (
                                <div className="flex-1 flex flex-col">
                                    <label className="block text-sm font-medium text-theme-main mb-1">Mensaje de Respuesta</label>
                                    {/* Message Editor Toolbar */}
                                    <MessageEditorToolbar
                                        textareaRef={messageTextareaRef}
                                        value={formData.response || ''}
                                        onChange={(value) => {
                                            setFormData({ ...formData, response: value });
                                            if (formErrors.response) setFormErrors({ ...formErrors, response: '' });
                                        }}
                                        variables={['nombre', 'pais']}
                                        showVariables={true}
                                        showEmojiPickerBelow={true}
                                    />
                                    <textarea
                                        ref={messageTextareaRef}
                                        className={`w-full min-h-[100px] max-h-[450px] p-4 border rounded-lg resize-none overflow-y-auto mt-2 focus:ring-2 focus:ring-primary-500 focus:border-transparent font-sans ${formErrors.response ? 'border-red-300' : 'border-theme'
                                            }`}
                                        placeholder="Escribe la respuesta automática aquí... (opcional si adjuntas multimedia)"
                                        value={formData.response}
                                        onChange={e => {
                                            setFormData({ ...formData, response: e.target.value });
                                            if (formErrors.response) setFormErrors({ ...formErrors, response: '' });
                                        }}
                                    ></textarea>
                                    <div className="mt-3">
                                        <label className="block text-xs font-semibold text-theme-main mb-1.5 text-primary-700">
                                            Destino del Mensaje Principal (Texto)
                                        </label>
                                        <select
                                            className="w-full border border-theme rounded-lg px-3 py-2 text-sm bg-theme-card focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 transition-all font-medium text-theme-main"
                                            value={formData.isMessageCaption ? 'caption' : 'text'}
                                            onChange={e => setFormData({ ...formData, isMessageCaption: e.target.value === 'caption' })}
                                        >
                                            <option value="text">Enviar como Mensaje de Texto Independiente</option>
                                            <option value="caption">Enviar como Caption (Subtítulo) de Imagen/Video</option>
                                        </select>
                                    </div>
                                    {formErrors.response && (
                                        <p className="mt-1 text-sm text-red-600">{formErrors.response}</p>
                                    )}

                                    {/* Inline Preview */}
                                    {formData.response && (
                                        <div className="mt-4">
                                            <MessagePreview
                                                message={formData.response}
                                                contactName="Usuario"
                                                inline={true}
                                                showContactInfo={false}
                                            />
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Media Upload (available for simple rules only) */}
                            {formData.type === 'simple' && (
                                <div className="border border-slate-200 dark:border-slate-800 rounded-xl p-4 bg-slate-50/50 dark:bg-slate-900/30">
                                    <label className="block text-sm font-bold text-theme-main mb-2">
                                        Archivo Multimedia (opcional)
                                    </label>
                                    <MediaUpload
                                        mediaItems={media.mediaItems}
                                        onMediaChange={media.setMediaItems}
                                        maxFiles={50}
                                        fileInputRef={media.fileInputRef}
                                        onFileSelect={media.handleFileSelect}
                                        onDrop={media.handleDrop}
                                        onOpenFileSelector={media.openFileSelector}
                                        onRemoveMedia={media.removeMedia}
                                        onUpdateCaption={media.updateCaption}
                                        uploadProgress={media.uploadProgress}
                                    />
                                    <p className="text-xs text-slate-400 mt-2">
                                        Si adjuntas un archivo, el mensaje de texto se enviará por separado después del archivo.
                                    </p>
                                </div>
                            )}
                            {/* AI Settings (only for AI type) */}
                            {formData.type === 'ai' && (
                                <div className="space-y-4 border border-purple-200 dark:border-purple-900 rounded-xl p-4 bg-purple-50/20 dark:bg-purple-950/10">
                                    <div className="flex items-center gap-2 text-purple-700 dark:text-purple-400 border-b border-purple-100 dark:border-purple-900/50 pb-2 mb-3">
                                        <Bot size={20} className="animate-bounce text-purple-600" />
                                        <h4 className="font-bold text-sm">Configuración del Asistente de IA</h4>
                                    </div>

                                    {/* Siempre responder con IA (Catch-all) */}
                                    <div className="flex items-start justify-between p-3.5 bg-purple-100/40 dark:bg-purple-950/20 rounded-xl border border-purple-200/50 dark:border-purple-900/50 transition-all duration-200 hover:shadow-sm">
                                        <div className="space-y-0.5 max-w-[80%]">
                                            <label className="text-sm font-bold text-purple-950 dark:text-purple-300 flex items-center gap-1.5 cursor-pointer">
                                                ✨ Siempre responder con esta IA (Catch-all)
                                            </label>
                                            <p className="text-sm text-purple-800/80 dark:text-purple-400/80 leading-relaxed">
                                                Si se activa, el bot responderá siempre con esta IA cuando el cliente escriba cualquier mensaje (sin necesidad de triggers específicos), actuando como tu asistente principal por defecto.
                                            </p>
                                        </div>
                                        <label className="relative inline-flex items-center cursor-pointer shrink-0 mt-1">
                                            <input
                                                type="checkbox"
                                                checked={formData.isCatchAll || false}
                                                onChange={e => setFormData({ ...formData, isCatchAll: e.target.checked })}
                                                className="sr-only peer"
                                            />
                                            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-purple-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-theme-card after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-600"></div>
                                        </label>
                                    </div>

                                    {/* Es Orquestador de IA */}
                                    <div className="flex items-start justify-between p-3.5 bg-purple-100/40 dark:bg-purple-950/20 rounded-xl border border-purple-200/50 dark:border-purple-900/50 transition-all duration-200 hover:shadow-sm">
                                        <div className="space-y-0.5 max-w-[80%]">
                                            <label className="text-sm font-bold text-purple-950 dark:text-purple-300 flex items-center gap-1.5 cursor-pointer">
                                                🧠 Es Orquestador (Enrutador)
                                            </label>
                                            <p className="text-sm text-purple-800/80 dark:text-purple-400/80 leading-relaxed">
                                                Si se activa, este agente actuará como un enrutador inteligente. Evaluará las consultas del usuario y las delegará automáticamente al experto especialista (otro agente de IA) con la base de conocimientos adecuada.
                                            </p>
                                        </div>
                                        <label className="relative inline-flex items-center cursor-pointer shrink-0 mt-1">
                                            <input
                                                type="checkbox"
                                                checked={formData.isOrchestrator || false}
                                                onChange={e => setFormData({ ...formData, isOrchestrator: e.target.checked })}
                                                className="sr-only peer"
                                            />
                                            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-purple-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-theme-card after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-600"></div>
                                        </label>
                                    </div>

                                    {/* Modo Estricto para el Orquestador */}
                                    {formData.isOrchestrator && (
                                        <div className="flex items-start justify-between p-3.5 bg-purple-100/40 dark:bg-purple-950/20 rounded-xl border border-purple-200/50 dark:border-purple-900/50 transition-all duration-200 hover:shadow-sm">
                                            <div className="space-y-0.5 max-w-[80%]">
                                                <label className="text-sm font-bold text-purple-950 dark:text-purple-300 flex items-center gap-1.5 cursor-pointer">
                                                    🔒 Modo Estricto
                                                </label>
                                                <p className="text-sm text-purple-800/80 dark:text-purple-400/80 leading-relaxed">
                                                    Si se activa, el orquestador y los agentes a los que delegue solo responderán si la información para contestar se encuentra explícitamente en alguna de sus bases de conocimientos. De lo contrario, el bot no responderá absolutamente nada.
                                                </p>
                                            </div>
                                            <label className="relative inline-flex items-center cursor-pointer shrink-0 mt-1">
                                                <input
                                                    type="checkbox"
                                                    checked={formData.orchestratorStrictMode || false}
                                                    onChange={e => setFormData({ ...formData, orchestratorStrictMode: e.target.checked })}
                                                    className="sr-only peer"
                                                />
                                                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-purple-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-theme-card after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-600"></div>
                                            </label>
                                        </div>
                                    )}

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-theme-main mb-1">Proveedor de IA</label>
                                            <select
                                                className="w-full border border-theme rounded-lg px-3 py-2 text-sm bg-theme-card focus:ring-purple-500 focus:border-purple-500 text-theme-main"
                                                value={['auto', 'google', 'openai', 'anthropic', 'xai'].includes(formData.aiProvider || '') ? (formData.aiProvider || 'auto') : 'auto'}
                                                onChange={e => {
                                                    const prov = e.target.value;
                                                    // El backend resuelve el modelo elegido cada 24 h según
                                                    // el catálogo real del proveedor. Al cambiar de proveedor
                                                    // dejamos explícitamente activa esa autodetección.
                                                    setFormData({ ...formData, aiProvider: prov, aiModel: 'auto' });
                                                }}
                                            >
                                                <option value="auto">✨ Auto-selección Inteligente (Recomendado)</option>
                                                <option value="google">Google Gemini</option>
                                                <option value="openai">OpenAI (ChatGPT)</option>
                                                <option value="anthropic">Anthropic (Claude)</option>
                                                <option value="xai">xAI (Grok)</option>
                                            </select>
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-theme-main mb-1">Modelo de IA</label>
                                            {(() => {
                                                const supportedProviders = ['auto', 'google', 'openai', 'anthropic', 'xai'];
                                                const prov = supportedProviders.includes(formData.aiProvider || '') ? (formData.aiProvider || 'google') : 'auto';
                                                const isCustomProvider = false;

                                                // Get static options for the provider
                                                let staticOptions: { value: string; label: string }[] = [];
                                                if (prov === 'auto') {
                                                    staticOptions = [{ value: 'auto', label: '🔍 auto-detectar modelo (Recomendado)' }];
                                                } else if (prov === 'google') {
                                                    staticOptions = [
                                                        { value: 'auto', label: '🔍 auto-detectar modelo (Flash-Lite más reciente)' },
                                                        { value: 'gemini-3.5-flash-lite', label: 'gemini-3.5-flash-lite (Flash-Lite más reciente)' },
                                                        { value: 'gemini-3.1-flash-lite', label: 'gemini-3.1-flash-lite (Flash-Lite)' },
                                                        { value: 'gemini-2.5-flash', label: 'gemini-2.5-flash (Rápido / Free Tier)' },
                                                        { value: 'gemini-2.5-pro', label: 'gemini-2.5-pro (Avanzado)' }
                                                    ];
                                                } else if (prov === 'openai') {
                                                    staticOptions = [
                                                        { value: 'auto', label: '🔍 auto-detectar modelo (Menor costo)' },
                                                        { value: 'gpt-4.1-nano', label: 'gpt-4.1-nano (Menor costo)' },
                                                        { value: 'gpt-4.1-mini', label: 'gpt-4.1-mini (Económico)' },
                                                        { value: 'gpt-4o-mini', label: 'gpt-4o-mini (Económico & Rápido)' },
                                                        { value: 'gpt-4o', label: 'gpt-4o (Multimodal Inteligente)' }
                                                    ];
                                                } else if (prov === 'anthropic') {
                                                    staticOptions = [
                                                        { value: 'auto', label: '🔍 auto-detectar modelo (Menor costo)' },
                                                        { value: 'claude-haiku-4-5', label: 'claude-haiku-4-5 (Menor costo)' },
                                                        { value: 'claude-sonnet-5', label: 'claude-sonnet-5 (Más capacidad)' },
                                                        { value: 'claude-sonnet-4-6', label: 'claude-sonnet-4-6 (Recomendado)' }
                                                    ];
                                                } else if (prov === 'xai') {
                                                    staticOptions = [
                                                        { value: 'auto', label: '🔍 auto-detectar modelo (Menor costo)' },
                                                        { value: 'grok-4.3', label: 'grok-4.3 (Menor costo)' },
                                                        { value: 'grok-4.6', label: 'grok-4.6 (Recomendado)' },
                                                        { value: 'grok-4.5', label: 'grok-4.5' }
                                                    ];
                                                }

                                                // Filter out any models already in static options to avoid duplicates
                                                const staticValues = staticOptions.map(o => o.value);
                                                const dynamicOptions = (modelsList || [])
                                                    .filter(m => !staticValues.includes(m))
                                                    .map(m => ({ value: m, label: m }));

                                                const allOptions = [...staticOptions, ...dynamicOptions];
                                                const currentVal = formData.aiModel || '';
                                                const isValInOptions = allOptions.some(o => o.value === currentVal);

                                                return (
                                                    <div className="space-y-2">
                                                        <select
                                                            className="w-full border border-theme rounded-lg px-3 py-2 text-sm bg-theme-card focus:ring-purple-500 focus:border-purple-500 text-theme-main"
                                                            value={isValInOptions ? currentVal : (currentVal ? 'custom' : 'auto')}
                                                            onChange={e => {
                                                                const val = e.target.value;
                                                                if (val === 'custom') {
                                                                    setFormData({ ...formData, aiModel: '' });
                                                                } else {
                                                                    setFormData({ ...formData, aiModel: val });
                                                                }
                                                            }}
                                                        >
                                                            {allOptions.map(opt => (
                                                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                                                            ))}
                                                            <option value="custom">Otro / Personalizado (Escribir ID de modelo)</option>
                                                        </select>

                                                        {(isCustomProvider || !isValInOptions || currentVal === 'custom') && (
                                                            <div>
                                                                <label className="block text-xs font-medium text-theme-muted mb-1">ID del Modelo Personalizado</label>
                                                                <input
                                                                    type="text"
                                                                    className="w-full border border-theme rounded-lg px-3 py-2 text-sm bg-theme-card focus:ring-purple-500 focus:border-purple-500 text-theme-main font-mono"
                                                                    placeholder="Ej: gpt-4-turbo o claude-3-opus"
                                                                    value={formData.aiModel === 'custom' ? '' : (formData.aiModel || '')}
                                                                    onChange={e => setFormData({ ...formData, aiModel: e.target.value })}
                                                                />
                                                                {isLoadingModels && <p className="text-[10px] text-purple-600 mt-1">🔄 Cargando lista de modelos dinámicos...</p>}
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            })()}
                                        </div>
                                    </div>

                                    {/* API Key Override (Optional) */}
                                    <div>
                                        <label className="block text-sm font-medium text-theme-main mb-1">
                                            API Key específica para esta Regla (Opcional)
                                        </label>
                                        <input
                                            type="password"
                                            className="w-full border border-theme rounded-lg px-3 py-2 text-sm bg-theme-card font-mono text-theme-main"
                                            placeholder="Sobreescribe la API Key configurada globalmente si es necesario..."
                                            value={formData.aiApiKey || ''}
                                            onChange={e => setFormData({ ...formData, aiApiKey: e.target.value })}
                                        />
                                    </div>

                                    {/* Prompts Templates Button */}
                                    <div>
                                        <label className="block text-sm font-medium text-theme-main mb-1.5">
                                            Plantillas de Prompt de Sistema (Un clic para cargar)
                                        </label>
                                        <div className="flex flex-wrap gap-2">
                                            {formData.isOrchestrator ? (
                                                <>
                                                    <button
                                                        type="button"
                                                        onClick={() => setFormData({
                                                            ...formData,
                                                            systemPrompt: 'Propósito global: El objetivo principal de todos los agentes es concretar ventas y cerrar tratos. Todas las interacciones deben estar orientadas a persuadir al cliente, destacar los beneficios y motivar el cierre de la compra o agendar una cita.'
                                                        })}
                                                        className="px-2.5 py-1 text-sm bg-purple-50 text-purple-700 dark:bg-purple-950/20 dark:text-purple-300 rounded-md border border-purple-200 dark:border-purple-900 hover:bg-purple-100 transition-colors font-medium shadow-xs"
                                                    >
                                                        🎯 Enfoque en Ventas
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => setFormData({
                                                            ...formData,
                                                            systemPrompt: 'Propósito global: El objetivo principal es la resolución de problemas y la satisfacción del cliente. Todos los agentes deben priorizar la paciencia, soporte detallado paso a paso y la fidelización del usuario.'
                                                        })}
                                                        className="px-2.5 py-1 text-sm bg-purple-50 text-purple-700 dark:bg-purple-950/20 dark:text-purple-300 rounded-md border border-purple-200 dark:border-purple-900 hover:bg-purple-100 transition-colors font-medium shadow-xs"
                                                    >
                                                        🤝 Soporte y Fidelización
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => setFormData({
                                                            ...formData,
                                                            systemPrompt: 'Propósito global: El objetivo principal es captar leads. Los agentes deben guiar la conversación para obtener datos de contacto (nombre, teléfono, correo) para transferir la conversación a un asesor humano.'
                                                        })}
                                                        className="px-2.5 py-1 text-sm bg-purple-50 text-purple-700 dark:bg-purple-950/20 dark:text-purple-300 rounded-md border border-purple-200 dark:border-purple-900 hover:bg-purple-100 transition-colors font-medium shadow-xs"
                                                    >
                                                        📞 Captación de Leads
                                                    </button>
                                                </>
                                            ) : (
                                                <>
                                                    <button
                                                        type="button"
                                                        onClick={() => setFormData({
                                                            ...formData,
                                                            systemPrompt: 'Eres un vendedor estrella de una tienda online. Responde amablemente, resuelve dudas sobre productos, precios y envíos de manera persuasiva y busca concretar la venta. Usa emojis y mantén las respuestas cortas.'
                                                        })}
                                                        className="px-2.5 py-1 text-sm bg-purple-50 text-purple-700 dark:bg-purple-950/20 dark:text-purple-300 rounded-md border border-purple-200 dark:border-purple-900 hover:bg-purple-100 transition-colors"
                                                    >
                                                        🛍️ Vendedor Online
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => setFormData({
                                                            ...formData,
                                                            systemPrompt: 'Eres un agente de ventas altamente persuasivo. Tu objetivo es concretar la venta del producto ofreciendo beneficios.\n\nRegla de Seguimiento por Inactividad:\n- Si la persona no responde en 1 minuto, vuelve a responder ofreciendo un descuento exclusivo del 10% por hoy.\n- Si no responde en los siguientes 5 minutos, lanza una oferta del 20% indicando que tiene un plazo máximo de 5 minutos para aprovecharla antes de que expire.\n- Si no responde en los siguientes 10 minutos, indícale de forma formal y cortés que la promoción ha finalizado, que es tu último llamado de cierre y despídete.'
                                                        })}
                                                        className="px-2.5 py-1 text-sm bg-purple-50 text-purple-700 dark:bg-purple-950/20 dark:text-purple-300 rounded-md border border-purple-200 dark:border-purple-900 hover:bg-purple-100 transition-colors font-medium shadow-xs"
                                                    >
                                                        📈 Ventas con Oferta Cascada
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => setFormData({
                                                            ...formData,
                                                            systemPrompt: 'Eres un agente de soporte técnico experto. Tu objetivo es guiar al usuario paso a paso para resolver su incidencia con paciencia, empatía y profesionalismo técnico de forma clara.'
                                                        })}
                                                        className="px-2.5 py-1 text-sm bg-purple-50 text-purple-700 dark:bg-purple-950/20 dark:text-purple-300 rounded-md border border-purple-200 dark:border-purple-900 hover:bg-purple-100 transition-colors"
                                                    >
                                                        🔧 Soporte Técnico
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => setFormData({
                                                            ...formData,
                                                            systemPrompt: 'Eres una asistente virtual de una clínica médica. Tu labor es agendar citas, informar sobre especialidades, horarios y requerimientos de forma sumamente educada y clara.'
                                                        })}
                                                        className="px-2.5 py-1 text-sm bg-purple-50 text-purple-700 dark:bg-purple-950/20 dark:text-purple-300 rounded-md border border-purple-200 dark:border-purple-900 hover:bg-purple-100 transition-colors"
                                                    >
                                                        🏥 Clínica Médica
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => setFormData({
                                                            ...formData,
                                                            systemPrompt: 'Eres un asistente administrativo encargado de confirmar citas agendadas.\n\nRegla de Seguimiento por Inactividad:\n- Si la persona no responde en 1 minuto para confirmar su asistencia, envíale un mensaje recordándole que su cupo se reservará temporalmente por 15 minutos más y que responda con SI para confirmar o NO para cancelar.\n- Si sigue sin responder pasados los 15 minutos, indícale de forma cortés que su cita ha sido liberada para dar espacio a otra persona y dile cómo reagendar en el futuro.'
                                                        })}
                                                        className="px-2.5 py-1 text-sm bg-purple-50 text-purple-700 dark:bg-purple-950/20 dark:text-purple-300 rounded-md border border-purple-200 dark:border-purple-900 hover:bg-purple-100 transition-colors font-medium shadow-xs"
                                                    >
                                                        📅 Confirmación de Citas
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => setFormData({
                                                            ...formData,
                                                            systemPrompt: 'Eres un experto en automatización e integración. Tienes la capacidad de llamar a herramientas externas usando el formato especial en tu respuesta: [APP_CALL: {"connection_id": ID, "params": {...}}].\n\nInstrucciones:\n1. Si el cliente solicita realizar una acción integrada (como registrar datos, despachar licencias de software o enviar alertas), responde invocando la herramienta respectiva.\n2. Asegúrate de incluir el connection_id correcto y los parámetros requeridos.\n3. Una vez procesada la llamada, el sistema te retornará el resultado para que confirmes la acción al cliente de manera clara.'
                                                        })}
                                                        className="px-2.5 py-1 text-sm bg-purple-50 text-purple-700 dark:bg-purple-950/20 dark:text-purple-300 rounded-md border border-purple-200 dark:border-purple-900 hover:bg-purple-100 transition-colors font-medium shadow-xs"
                                                    >
                                                        🔌 Agente de Integraciones (APIs)
                                                    </button>
                                                    {(() => {
                                                        const botadeskConn = connections.find(c => c.platform === 'botadesk' && c.is_enabled);
                                                        if (!botadeskConn) return null;
                                                        return (
                                                            <>
                                                                <button
                                                                    type="button"
                                                                    onClick={() => setFormData({
                                                                        ...formData,
                                                                        systemPrompt: `Eres un chatbot experto y automatizado encargado de la creación de cuentas de Office/Microsoft 365 a través de BotAdesk.\n\nTienes la capacidad de llamar a herramientas externas usando el formato especial en tu respuesta: \n[APP_CALL: {"connection_id": ${botadeskConn.id}, "params": {...}}]\n\nReglas de negocio obligatorias para los parámetros:\n1. **Plataforma**: Siempre usa "platform": "microsoft".\n2. **Correo / Nombre de Usuario ("email")**:\n   - Si el cliente te proporciona un correo completo, úsalo.\n   - Si el cliente NO te da un correo pero sabes su nombre (ej: "Juan Pérez"), genera un nombre de usuario local con la primera letra de su nombre y su apellido completo (ej: "juan.perez").\n   - Si no tienes su nombre ni su correo, genera un nombre de usuario local usando el prefijo "usr_" seguido de su número de teléfono sin el signo "+" (ej: "usr_51999888777").\n3. **Contraseña fija**: Siempre establece el parámetro "password" como "2748Curso".\n4. **Forzar cambio de contraseña**: Siempre establece el parámetro "force_password_change" en false.\n5. **Ubicación / País ("country")**:\n   - Si el cliente menciona su país, conviértelo a su código ISO de 2 letras en mayúsculas para el campo "country" (ej: "Perú" -> "PE", "México" -> "MX", "Colombia" -> "CO").\n   - Si el cliente NO menciona un país, usa "PE" por defecto.\n6. **Licencia predeterminada**: Siempre incluye el parámetro "sku_id": "default".\n7. **Búsqueda de Inquilino / Empresa ("tenant_search")**:\n   - Si el cliente menciona un nombre de organización o dominio (como "ldci", "ultb" o "ievinh"), colócalo en el parámetro "tenant_search" para que BotAdesk busque y asigne el panel correspondiente. Si no se menciona nada, omite este parámetro.\n\nFlujo de Operación:\n- Inicia la solicitud llamando a la herramienta de BotAdesk con el connection_id correspondiente.\n- Ejemplo de trigger a generar:\n  [APP_CALL: {"connection_id": ${botadeskConn.id}, "params": {"platform": "microsoft", "email": "juan.perez", "password": "2748Curso", "force_password_change": false, "sku_id": "default", "country": "PE", "tenant_search": "ldci"}}]\n- Cuando el sistema procese la llamada y retorne el resultado exitoso con el UPN final asignado y la contraseña, confirma la acción al cliente de forma clara y cordial entregándole sus credenciales de acceso.`
                                                                    })}
                                                                    className="px-2.5 py-1 text-sm bg-blue-50 text-blue-700 dark:bg-blue-950/20 dark:text-blue-300 rounded-md border border-blue-200 dark:border-blue-900 hover:bg-blue-100 transition-colors font-medium shadow-xs"
                                                                >
                                                                    🔑 Office (BotAdesk)
                                                                </button>
                                                                <button
                                                                    type="button"
                                                                    onClick={() => setFormData({
                                                                        ...formData,
                                                                        systemPrompt: `Eres un chatbot experto y automatizado encargado de procesar solicitudes de invitaciones de Autodesk a través de BotAdesk.\n\nTienes la capacidad de llamar a herramientas externas usando el formato especial en tu respuesta: \n[APP_CALL: {"connection_id": ${botadeskConn.id}, "params": {...}}]\n\nReglas de negocio obligatorias para los parámetros:\n1. **Plataforma**: Siempre usa "platform": "autodesk".\n2. **Correo ("email")**: Es obligatorio solicitar y extraer el correo electrónico del cliente para registrar su invitación en la cola de procesamiento.\n3. **Ubicación / País ("country")**:\n   - Si el cliente menciona su país, conviértelo a su código ISO de 2 letras en mayúsculas para el campo "country" (ej: "Perú" -> "PE", "México" -> "MX", "Colombia" -> "CO").\n   - Si el cliente NO menciona un país, usa "PE" por defecto.\n4. **Notas ("notes")**: Opcionalmente, puedes añadir información sobre la solicitud (ej: "Invitación Autodesk Solicitada").\n\nFlujo de Operación:\n- Solicita el correo electrónico del cliente si no lo tienes.\n- Invoca la herramienta de BotAdesk con el connection_id correspondiente.\n- Ejemplo de trigger a generar:\n  [APP_CALL: {"connection_id": ${botadeskConn.id}, "params": {"platform": "autodesk", "email": "usuario@correo.com", "country": "PE", "notes": "Invitación Autodesk"}}]\n- Informa al cliente que su invitación ha sido encolada con éxito y que se le enviará un mensaje cuando el agente complete el procesamiento.`
                                                                    })}
                                                                    className="px-2.5 py-1 text-sm bg-amber-50 text-amber-700 dark:bg-amber-950/20 dark:text-amber-300 rounded-md border border-amber-200 dark:border-amber-900 hover:bg-amber-100 transition-colors font-medium shadow-xs"
                                                                >
                                                                    📐 Autodesk (BotAdesk)
                                                                </button>
                                                                <button
                                                                    type="button"
                                                                    onClick={() => setFormData({
                                                                        ...formData,
                                                                        systemPrompt: `Eres un chatbot experto encargado de crear cuentas de Office (Microsoft 365) e invitaciones de Autodesk en BotAdesk.\n\nTienes la capacidad de llamar a herramientas externas usando el formato especial en tu respuesta: \n[APP_CALL: {"connection_id": ${botadeskConn.id}, "params": {...}}]\n\nReglas de negocio obligatorias para los parámetros:\n1. **Para Microsoft 365 (Office)**:\n   - "platform": "microsoft"\n   - "email": Correo del cliente o nombre de usuario local generado (ej: "juan.perez" o "usr_51999888777").\n   - "password": Fijo en "2748Curso".\n   - "force_password_change": false.\n   - "country": Código ISO de 2 letras (ej: "PE").\n   - "sku_id": "default".\n   - "tenant_search": Opcional. Búsqueda de palabra clave o dominio de la organización (ej: "ldci").\n2. **Para Autodesk**:\n   - "platform": "autodesk"\n   - "email": Correo electrónico real obligatorio del cliente.\n   - "country": Código ISO de 2 letras.\n\nFlujo de Operación:\n- Si el cliente pide cuenta de Office: invoca [APP_CALL: {"connection_id": ${botadeskConn.id}, "params": {"platform": "microsoft", "email": "nombre.usuario", "password": "2748Curso", "force_password_change": false, "sku_id": "default", "country": "PE", "tenant_search": "ldci"}}]\n- Si el cliente pide cuenta de Autodesk: invoca [APP_CALL: {"connection_id": ${botadeskConn.id}, "params": {"platform": "autodesk", "email": "correo@real.com", "country": "PE"}}]\n- Una vez recibido el resultado, responde con confirmación detallada.`
                                                                    })}
                                                                    className="px-2.5 py-1 text-sm bg-emerald-50 text-emerald-700 dark:bg-emerald-950/20 dark:text-emerald-300 rounded-md border border-emerald-200 dark:border-emerald-900 hover:bg-emerald-100 transition-colors font-medium shadow-xs"
                                                                >
                                                                    🔄 Ambas (BotAdesk)
                                                                </button>
                                                            </>
                                                        );
                                                    })()}
                                                </>
                                            )}
                                        </div>
                                    </div>

                                    {/* System Prompt Textarea */}
                                    <div>
                                        <label className="block text-sm font-medium text-theme-main mb-1">
                                            System Prompt (Instrucciones de la IA)
                                        </label>
                                        <textarea
                                            className={`w-full min-h-[140px] max-h-[350px] px-4 py-2 border rounded-lg text-base font-sans bg-theme-card text-theme-main focus:ring-purple-500 focus:border-purple-500 ${formErrors.systemPrompt ? 'border-red-300' : 'border-theme'
                                                }`}
                                            placeholder="Define la personalidad e instrucciones para el Asistente IA..."
                                            value={formData.systemPrompt || ''}
                                            onChange={e => {
                                                setFormData({ ...formData, systemPrompt: e.target.value });
                                                if (formErrors.systemPrompt) setFormErrors({ ...formErrors, systemPrompt: '' });
                                            }}
                                        ></textarea>
                                        {formErrors.systemPrompt && (
                                            <p className="mt-1 text-sm text-red-600">{formErrors.systemPrompt}</p>
                                        )}
                                    </div>

                                    {/* Plain text knowledge base */}
                                    <div>
                                        <div className="flex justify-between items-center mb-1">
                                            <label className="block text-sm font-medium text-theme-main">
                                                Base de Conocimiento en Texto Plano (Copiar y Pegar)
                                            </label>
                                            <button
                                                type="button"
                                                disabled={isOptimizingKB || isDownloadingModel || !formData.knowledgeBaseText || formData.knowledgeBaseText.trim() === ''}
                                                onClick={() => {
                                                    if (!formData.knowledgeBaseText || formData.knowledgeBaseText.trim() === '') {
                                                        if (toast) toast.warning('Escribe o pega información en la base de conocimientos primero.');
                                                        return;
                                                    }
                                                    setShowOptimizeKbModal(true);
                                                }}
                                                className="flex items-center gap-1.5 px-3 py-1 bg-purple-100 hover:bg-purple-200 active:bg-purple-300 dark:bg-purple-900/50 dark:hover:bg-purple-900 dark:text-purple-300 text-purple-700 rounded-lg text-xs font-semibold shadow-sm border border-purple-200/50 dark:border-purple-800 transition-all hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:scale-100"
                                                title="Resume y optimiza el texto con IA (usa window.ai local de tu navegador si está disponible, o recurre a tu API del servidor)"
                                            >
                                                {isDownloadingModel ? (
                                                    <>
                                                        <Loader size={12} className="animate-spin text-purple-600 dark:text-purple-400" />
                                                        <span>
                                                            Descargando IA: {downloadProgress
                                                                ? `${Math.round((downloadProgress.loaded / downloadProgress.total) * 100)}%`
                                                                : 'Iniciando...'}
                                                        </span>
                                                    </>
                                                ) : isOptimizingKB ? (
                                                    <>
                                                        <Loader size={12} className="animate-spin text-purple-600 dark:text-purple-400" />
                                                        <span>Optimizando Base...</span>
                                                    </>
                                                ) : (
                                                    <>
                                                        <Sparkles size={12} className="text-purple-600 dark:text-purple-400" />
                                                        <span>Optimizar con IA</span>
                                                    </>
                                                )}
                                            </button>
                                        </div>
                                        {isDownloadingModel && (
                                            <div className="mb-2 p-3 bg-purple-50 dark:bg-purple-950/20 border border-purple-200 dark:border-purple-900 rounded-lg animate-pulse flex flex-col gap-1.5 text-xs text-purple-800 dark:text-purple-300">
                                                <div className="flex justify-between items-center font-semibold">
                                                    <span>📥 Descargando IA local Gemini Nano en tu navegador...</span>
                                                    <span>
                                                        {downloadProgress
                                                            ? `${(downloadProgress.loaded / (1024 * 1024)).toFixed(1)} MB / ${(downloadProgress.total / (1024 * 1024)).toFixed(1)} MB`
                                                            : 'Iniciando descarga...'}
                                                    </span>
                                                </div>
                                                <div className="w-full bg-purple-200 dark:bg-purple-900 rounded-full h-2 overflow-hidden">
                                                    <div
                                                        className="bg-purple-600 dark:bg-purple-400 h-full transition-all duration-300 rounded-full"
                                                        style={{ width: `${downloadProgress ? Math.min(100, Math.round((downloadProgress.loaded / downloadProgress.total) * 100)) : 0}%` }}
                                                    ></div>
                                                </div>
                                                <p className="text-[10px] text-purple-600/80 dark:text-purple-400/80">
                                                    Esta descarga es automática, gratuita y solo se realiza una vez. Por favor no cierres ni recargues esta pestaña hasta que finalice.
                                                </p>
                                            </div>
                                        )}
                                        <textarea
                                            className="w-full min-h-[160px] max-h-[350px] px-4 py-2 border border-theme rounded-lg text-base font-sans bg-theme-card text-theme-main focus:ring-purple-500 focus:border-purple-500 transition-all"
                                            placeholder="Pega aquí la información, preguntas frecuentes, catálogo de productos o cualquier texto que quieras que la IA use para responder..."
                                            value={formData.knowledgeBaseText || ''}
                                            onChange={e => setFormData({ ...formData, knowledgeBaseText: e.target.value })}
                                        ></textarea>
                                    </div>

                                    {/* Knowledge Base Info (RAG) */}
                                    <div className="p-3 bg-purple-50 dark:bg-purple-950/20 border border-purple-200/50 dark:border-purple-900/50 rounded-lg space-y-2">
                                        <h5 className="text-sm font-bold text-purple-950 dark:text-purple-300 flex items-center gap-1.5">
                                            <FileText size={14} /> Base de Conocimiento Activa (.txt, .md, .csv)
                                        </h5>
                                        <p className="text-sm text-purple-800 dark:text-purple-400 leading-relaxed">
                                            ¿Quieres que el Asistente responda sobre tu negocio, catálogo o preguntas frecuentes?
                                            ¡Adjunta archivos en la sección de abajo!
                                            Los archivos de texto plano que subas serán leídos automáticamente en el servidor y entregados a la IA como su fuente de verdad.
                                        </p>
                                    </div>

                                    {/* Multimodal Info */}
                                    <div className="p-3 bg-blue-50 dark:bg-blue-950/20 border border-blue-200/50 dark:border-blue-900/50 rounded-lg space-y-2">
                                        <h5 className="text-sm font-bold text-blue-950 dark:text-blue-300 flex items-center gap-1.5">
                                            <Image size={14} /> Capacidad Multimodal Habilitada
                                        </h5>
                                        <p className="text-sm text-blue-800 dark:text-blue-400 leading-relaxed">
                                            Si el cliente te envía una <strong>foto</strong>, un <strong>video</strong> o un <strong>audio (nota de voz)</strong>, los modelos compatibles los procesarán de forma nativa para dar una respuesta inteligente.
                                        </p>
                                    </div>

                                    {/* Knowledge Base files upload section */}
                                    <div className="border border-purple-200 dark:border-purple-900 rounded-xl p-4 bg-purple-50/10 dark:bg-purple-950/5 mt-4">
                                        <label className="block text-sm font-bold text-purple-950 dark:text-purple-300 mb-2 flex items-center gap-1.5">
                                            <FileText size={16} /> Documentos de Base de Conocimiento (.txt, .md, .csv)
                                        </label>
                                        <p className="text-xs text-slate-400 mb-3">
                                            Los archivos subidos aquí se leerán y se usarán como contexto / fuente de información exclusiva para responder al usuario.
                                        </p>
                                        <MediaUpload
                                            accept=".txt,.md,.csv"
                                            mediaItems={kbMedia.mediaItems}
                                            onMediaChange={kbMedia.setMediaItems}
                                            maxFiles={50}
                                            fileInputRef={kbMedia.fileInputRef}
                                            onFileSelect={kbMedia.handleFileSelect}
                                            onDrop={kbMedia.handleDrop}
                                            onOpenFileSelector={kbMedia.openFileSelector}
                                            onRemoveMedia={kbMedia.removeMedia}
                                            onUpdateCaption={kbMedia.updateCaption}
                                            uploadProgress={kbMedia.uploadProgress}
                                        />
                                    </div>

                                    {/* Support files upload section */}
                                    <div className="border border-blue-200 dark:border-blue-900 rounded-xl p-4 bg-blue-50/10 dark:bg-blue-950/5 mt-4">
                                        <label className="block text-sm font-bold text-blue-950 dark:text-blue-300 mb-2 flex items-center gap-1.5">
                                            <Image size={16} /> Flyers y Archivos Multimedia de Soporte
                                        </label>
                                        <p className="text-xs text-slate-400 mb-3">
                                            Sube imágenes, flyers o videos. El chatbot los enviará al usuario si los solicita o si pregunta por ellos. ¡Añádeles un título o descripción informativa para que la IA sepa qué contiene cada archivo!
                                        </p>
                                        <MediaUpload
                                            accept="image/*,video/*,.pdf"
                                            mediaItems={flyerMedia.mediaItems}
                                            onMediaChange={flyerMedia.setMediaItems}
                                            maxFiles={50}
                                            fileInputRef={flyerMedia.fileInputRef}
                                            onFileSelect={flyerMedia.handleFileSelect}
                                            onDrop={flyerMedia.handleDrop}
                                            onOpenFileSelector={flyerMedia.openFileSelector}
                                            onRemoveMedia={flyerMedia.removeMedia}
                                            onUpdateCaption={flyerMedia.updateCaption}
                                            uploadProgress={flyerMedia.uploadProgress}
                                        />
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="pt-6 mt-4 border-t border-theme flex justify-end">
                            <button
                                onClick={handleSave}
                                className="bg-slate-900 dark:bg-slate-100 text-white dark:text-slate-900 px-8 py-3 rounded-lg font-medium hover:bg-black dark:hover:bg-white flex items-center gap-2 shadow-lg transition-all duration-200"
                            >
                                <Save size={18} /> {editingId ? 'Actualizar Regla' : 'Guardar Regla'}
                            </button>
                        </div>
                    </div>
                </div>
            </div >

            {/* Delete Confirmation Modal */}
            < ConfirmModal
                isOpen={showDeleteModal}
                onClose={() => {
                    setShowDeleteModal(false);
                    setRuleToDelete(null);
                }}
                onConfirm={confirmDelete}
                title="Eliminar Regla"
                message={ruleToDelete ? `¿Estás seguro de que deseas eliminar la regla "${rules.find(r => r.id === ruleToDelete)?.name || 'esta regla'}"? Esta acción no se puede deshacer.` : ''}
                confirmText="Eliminar"
                cancelText="Cancelar"
                type="danger"
            />

            <ImportModal
                isOpen={showImportModal}
                onClose={() => setShowImportModal(false)}
                onImport={handleImportFromModal}
                title="Importar Reglas de Auto-Respuesta"
                description="Selecciona un archivo ZIP o JSON con reglas"
                acceptedFormats=".json,.zip"
            />

            <BulkRuleCreator
                isOpen={showBulkCreator}
                onClose={() => setShowBulkCreator(false)}
                onRulesCreated={(newRules) => {
                    setRules([...rules, ...newRules]);
                    if (newRules.length > 0) {
                        setTimeout(() => {
                            const firstNewRule = newRules[0];
                            const element = document.getElementById(`rule-container-${firstNewRule.id}`);
                            if (element) {
                                element.scrollIntoView({ behavior: 'smooth', block: 'center' });
                            }
                        }, 100);
                    }
                }}
                toast={toast}
            />

            {/* Optimize Knowledge Base Modal (Selection & Language) */}
            {showOptimizeKbModal && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                    <div className="bg-theme-card rounded-2xl shadow-2xl max-w-md w-full border border-theme overflow-hidden animate-fade-in flex flex-col">
                        {/* Header */}
                        <div className="p-5 bg-gradient-to-r from-purple-600 to-indigo-600 text-white flex items-center justify-between shrink-0">
                            <h4 className="font-bold text-base flex items-center gap-2">
                                <Sparkles size={18} className="animate-pulse" />
                                Ajustes de Optimización con IA
                            </h4>
                            <button
                                onClick={() => setShowOptimizeKbModal(false)}
                                className="p-1 hover:bg-white/20 rounded-full transition-colors"
                            >
                                <X size={16} />
                            </button>
                        </div>

                        {/* Content */}
                        <div className="p-5 space-y-4 text-sm text-theme-main">
                            {/* Method Selector */}
                            <div className="space-y-2">
                                <label className="block font-medium text-xs text-theme-muted uppercase tracking-wider">
                                    Método de Optimización
                                </label>
                                <div className="grid grid-cols-2 gap-3">
                                    <button
                                        type="button"
                                        onClick={() => setOptMethod('local')}
                                        className={`p-3 rounded-xl border flex flex-col items-center gap-1.5 transition-all ${optMethod === 'local'
                                            ? 'border-purple-500 bg-purple-50/50 dark:bg-purple-950/20 text-purple-700 dark:text-purple-300 ring-2 ring-purple-500/20'
                                            : 'border-theme hover:bg-slate-50 dark:hover:bg-slate-800'
                                            }`}
                                    >
                                        <Zap size={20} className={optMethod === 'local' ? 'text-purple-600' : 'text-slate-400'} />
                                        <span className="font-semibold text-xs">IA Local (Gratis)</span>
                                        <span className="text-[10px] text-center text-slate-400">Gemini Nano en Chrome</span>
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setOptMethod('server')}
                                        className={`p-3 rounded-xl border flex flex-col items-center gap-1.5 transition-all ${optMethod === 'server'
                                            ? 'border-purple-500 bg-purple-50/50 dark:bg-purple-950/20 text-purple-700 dark:text-purple-300 ring-2 ring-purple-500/20'
                                            : 'border-theme hover:bg-slate-50 dark:hover:bg-slate-800'
                                            }`}
                                    >
                                        <Bot size={20} className={optMethod === 'server' ? 'text-purple-600' : 'text-slate-400'} />
                                        <span className="font-semibold text-xs">IA de mi Servidor</span>
                                        <span className="text-[10px] text-center text-slate-400">Rápida (usa API Key)</span>
                                    </button>
                                </div>
                            </div>

                            {/* Language Selector */}
                            <div className="space-y-1.5">
                                <label className="block font-medium text-xs text-theme-muted uppercase tracking-wider">
                                    Idioma de Salida
                                </label>
                                <select
                                    value={optLanguage}
                                    onChange={(e) => setOptLanguage(e.target.value)}
                                    className="w-full border border-theme rounded-xl px-3 py-2 text-sm bg-theme-card text-theme-main focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                                >
                                    <option value="Español">🇪🇸 Español (Por defecto)</option>
                                    <option value="Inglés">🇺🇸 Inglés</option>
                                    <option value="Portugués">🇧🇷 Portugués</option>
                                    <option value="Francés">🇫🇷 Francés</option>
                                </select>
                            </div>
                        </div>

                        {/* Footer */}
                        <div className="p-4 bg-theme-base border-t border-theme flex gap-3">
                            <button
                                type="button"
                                onClick={() => {
                                    setShowOptimizeKbModal(false);
                                    handleOptimizeKB(optMethod, optLanguage);
                                }}
                                className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white font-semibold rounded-xl shadow-md transition-all active:scale-95"
                            >
                                <Sparkles size={14} />
                                Iniciar Optimización
                            </button>
                            <button
                                type="button"
                                onClick={() => setShowOptimizeKbModal(false)}
                                className="px-4 py-2.5 border border-theme hover:bg-slate-50 dark:hover:bg-slate-800 text-theme-main font-semibold rounded-xl transition-all"
                            >
                                Cancelar
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Browser AI Activation & Fallback Modal */}
            {showBrowserAiModal && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                    <div className="bg-theme-card rounded-2xl shadow-2xl max-w-lg w-full border border-theme overflow-hidden animate-fade-in flex flex-col max-h-[90vh]">
                        {/* Header */}
                        <div className="p-6 bg-gradient-to-r from-purple-600 to-indigo-600 text-white flex items-center justify-between shrink-0">
                            <h4 className="font-bold text-lg flex items-center gap-2">
                                <Sparkles size={20} className="animate-pulse" />
                                Optimizar Base con IA Local
                            </h4>
                            <button
                                onClick={() => setShowBrowserAiModal(false)}
                                className="p-1 hover:bg-white/20 rounded-full transition-colors"
                            >
                                <X size={18} />
                            </button>
                        </div>

                        {/* Content */}
                        <div className="p-6 overflow-y-auto space-y-5 text-sm leading-relaxed custom-scrollbar">
                            <div className="bg-purple-50 dark:bg-purple-950/20 p-4 border border-purple-200/50 dark:border-purple-900/50 rounded-xl space-y-2">
                                <p className="font-bold text-purple-950 dark:text-purple-300">
                                    💡 ¿Sabías que puedes optimizar gratis y sin internet?
                                </p>
                                <p className="text-purple-800 dark:text-purple-400 text-xs">
                                    Google Chrome incluye un modelo de Inteligencia Artificial local llamado <strong>Gemini Nano</strong>. Si lo habilitas, WhatyBot optimizará tus textos al instante de forma 100% gratuita y sin consumir tus claves de API.
                                </p>
                            </div>

                            <div className="space-y-3">
                                <h5 className="font-bold text-theme-main flex items-center gap-1.5 text-xs uppercase tracking-wider text-slate-500">
                                    🛠️ Pasos para habilitar Gemini Nano en Chrome:
                                </h5>
                                <ol className="list-decimal list-inside space-y-2.5 text-xs text-theme-muted bg-theme-base p-4 rounded-xl border border-theme">
                                    <li>Escribe <code className="bg-slate-200 dark:bg-slate-800 px-1 py-0.5 rounded font-mono select-all">chrome://flags</code> en una pestaña y presiona Enter (¡Ya habilitaste los flags! ✅).</li>
                                    <li>Abre <code className="bg-slate-200 dark:bg-slate-800 px-1 py-0.5 rounded font-mono select-all">chrome://components</code> en una nueva pestaña.</li>
                                    <li>Busca el componente <strong>"Optimization Guide On Device Model"</strong> y haz clic en el botón <strong>"Buscar actualizaciones"</strong> (Check for update) para descargar el modelo Gemini Nano a tu PC inmediatamente (pesa ~1.5 GB).</li>
                                    <li>Espera hasta que el estado cambie a <strong>"Componente actualizado"</strong> o finalice la descarga, y luego recarga esta pestaña para comenzar.</li>
                                </ol>
                            </div>

                            <p className="text-theme-muted text-xs border-t border-theme pt-4">
                                ¿No deseas activarlo ahora o usas otro navegador? No te preocupes, puedes usar el servidor seguro de WhatyBot con las claves de API que tienes configuradas.
                            </p>
                        </div>

                        {/* Actions */}
                        <div className="p-6 bg-theme-base border-t border-theme flex flex-col sm:flex-row gap-3 shrink-0">
                            <button
                                type="button"
                                onClick={() => {
                                    setShowBrowserAiModal(false);
                                    executeServerOptimization();
                                }}
                                className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white font-semibold rounded-xl shadow-md transition-all active:scale-95"
                            >
                                <Bot size={16} />
                                Usar IA de mi Servidor
                            </button>
                            <button
                                type="button"
                                onClick={() => setShowBrowserAiModal(false)}
                                className="flex-1 px-4 py-2.5 border border-theme hover:bg-slate-100 dark:hover:bg-slate-800 text-theme-main font-semibold rounded-xl transition-all"
                            >
                                Cancelar
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};
